<template>
  <div v-if="competition && competitionRounds" class="competition-judge" >
    <div class="header-row">
      <div>
        <h3 class="mb-2">{{ panelMode === 'assignment' && !allowRecording ? translateOr('judge.assignment.title', 'Asignacion de rondas') : (allowRecording ? $t('competitions.video_table_title') : $t("judge.title")) }}</h3>
      </div>
      <div v-if="canManageAssignments && !allowRecording" class="judge-mode-toggle btn-group btn-group-sm" role="group" aria-label="Judge panel mode">
        <button
          type="button"
          class="btn"
          :class="panelMode === 'operations' ? 'btn-primary' : 'btn-outline-primary'"
          @click="panelMode = 'operations'"
        >
          {{ translateOr('judge.operations.tab', 'Operacion') }}
        </button>
        <button
          type="button"
          class="btn"
          :class="panelMode === 'assignment' ? 'btn-primary' : 'btn-outline-primary'"
          @click="panelMode = 'assignment'"
        >
          {{ translateOr('judge.assignment.tab', 'Asignacion') }}
        </button>
      </div>
    </div>

    <div v-if="competitionRounds.length === 0" class="no-athletes">
      {{ $t("judge.no_athletes") }}
    </div>

    <section
      v-if="showJudgeOperationsAssignmentBanner"
      class="judge-operations-banner"
    >
      <div class="judge-operations-banner__header">
        <div>
          <p class="judge-operations-banner__eyebrow">{{ translateOr('judge.operations.eyebrow', 'Panel operativo') }}</p>
          <h4>{{ selectedAssignmentPhaseLabel }}</h4>
          <p>{{ judgeOperationsBannerSummary }}</p>
        </div>
        <div class="judge-operations-banner__summary-pill">
          <strong>{{ operationAssignmentCards.length }}</strong>
          <span>{{ translateOr('judge.operations.active_rounds', 'rondas activas') }}</span>
        </div>
      </div>

      <div class="judge-operations-banner__track" role="list" :aria-label="translateOr('judge.operations.track_aria', 'Rondas asignadas')">
        <article
          v-for="card in operationAssignmentCards"
          :key="`operation-card-${card.id}`"
          class="judge-operations-card"
          :class="[
            `judge-operations-card--${card.tone}`,
            { 'judge-operations-card--clickable': card.canOpenScore },
          ]"
          role="listitem"
          :tabindex="card.canOpenScore ? 0 : undefined"
          @click="handleOperationCardClick(card)"
          @keyup.enter="handleOperationCardClick(card)"
        >
          <div class="judge-operations-card__topline">
            <span class="judge-operations-card__priority">{{ card.priorityLabel }}</span>
            <span class="judge-operations-card__status">{{ card.statusLabel }}</span>
          </div>

          <div class="judge-operations-card__body">
            <strong>{{ card.title }}</strong>
            <p>{{ card.assignmentCountdown || card.subtitle }}</p>
          </div>

          <div class="judge-operations-card__footer">
            <span class="judge-operations-card__judge">{{ card.judgeLabel }}</span>
            <span class="judge-operations-card__position">#{{ card.position }}</span>
          </div>
        </article>
      </div>
    </section>

    <div
      v-if="allowRecording && panelMode === 'operations'"
      class="recording-top-row"
      :class="{
        'recording-top-row--single': activeTimerControls.length > 0 && !isVsTimerContext,
        'recording-top-row--vs': isVsTimerContext && activeTimerControls.length > 1,
      }"
    >
      <div
        v-if="!isVsTimerContext && activeTimerControls.length"
        class="timer-control-panel timer-control-panel--compact timer-control-panel--single"
      >
        <div class="timer-control-cards timer-control-cards--stacked">
          <div
            v-for="ctrl in activeTimerControls"
            :key="ctrl.roundId"
            class="timer-control-card"
          >
            <div class="timer-control-card__header">
              <span class="timer-control-card__label">{{ ctrl.label }}</span>
            </div>
            <div class="timer-control-card__pill">{{ ctrl.display }}</div>
            <div class="timer-control-card__actions">
              <button
                class="timer-toggle-btn"
                :class="ctrl.isRunning ? 'is-running' : 'is-idle'"
                :disabled="ctrl.disableAll"
                @click.stop="toggleTimerControl(ctrl)"
              >
                <i :class="ctrl.isRunning ? 'fa-solid fa-pause' : 'fa-solid fa-play'"></i>
              </button>
            </div>
            <div class="timer-control-card__footer">
              <span class="timer-control-card__state" :class="`state-${ctrl.state}`">{{ ctrl.stateLabel }}</span>
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="isVsTimerContext && activeTimerControls[0]"
        class="timer-control-panel timer-control-panel--compact timer-control-panel--side"
      >
        <div class="timer-control-cards timer-control-cards--stacked">
          <div class="timer-control-card">
            <div class="timer-control-card__header">
              <span class="timer-control-card__label">{{ activeTimerControls[0].label }}</span>
            </div>
            <div class="timer-control-card__pill">{{ activeTimerControls[0].display }}</div>
            <div class="timer-control-card__actions">
              <button
                class="timer-toggle-btn"
                :class="activeTimerControls[0].isRunning ? 'is-running' : 'is-idle'"
                :disabled="activeTimerControls[0].disableAll"
                @click.stop="toggleTimerControl(activeTimerControls[0])"
              >
                <i :class="activeTimerControls[0].isRunning ? 'fa-solid fa-pause' : 'fa-solid fa-play'"></i>
              </button>
            </div>
            <div class="timer-control-card__footer">
              <span class="timer-control-card__state" :class="`state-${activeTimerControls[0].state}`">{{ activeTimerControls[0].stateLabel }}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="recording-inline recording-inline--priority">
        <CameraModal
          :visible="true"
          :title="$t('judge.camera.modal_title')"
          :helper="$t('judge.camera.helper')"
          :selectLabel="$t('judge.camera.select')"
          :noDevicesLabel="$t('judge.camera.no_devices')"
          :previewPlaceholder="$t('judge.camera.preview_placeholder')"
          :startCameraLabel="$t('judge.camera.start')"
          :recordLabel="$t('judge.camera.record')"
          :stopLabel="$t('judge.camera.stop')"
          :cancelLabel="''"
          :loadingLabel="$t('judge.camera.loading')"
          :savingLabel="$t('judge.camera.saving')"
          :noDataLabel="$t('judge.camera.no_data')"
          :stopCameraLabel="$t('judge.camera.stop_camera')"
          :qualityLabel="$t('judge.camera.quality_label')"
          :qualityLowLabel="$t('judge.camera.quality_low')"
          :qualityMediumLabel="$t('judge.camera.quality_medium')"
          :qualityMaxLabel="$t('judge.camera.quality_max')"
          :qualityProLabel="$t('judge.camera.quality_pro')"
          :statusRecordingLabel="$t('judge.camera.status_recording')"
          :statusReadyLabel="$t('judge.camera.status_ready')"
          :statusOffLabel="$t('judge.camera.status_off')"
          :actualLabel="$t('judge.camera.info_actual')"
          :targetLabel="$t('judge.camera.info_target')"
          :supportsLabel="$t('judge.camera.info_supports')"
          :autoLabel="$t('judge.camera.info_auto')"
          :styleLabel="$t('judge.camera.style_label')"
          :styleLightLabel="$t('judge.camera.style_light')"
          :styleDarkLabel="$t('judge.camera.style_dark')"
          :styleCustomLabel="$t('judge.camera.style_custom')"
          :liveActive="liveRoundViewerActive"
          :liveBusy="liveServiceBusy"
          :liveTooltipLabel="liveToggleTooltip"
          :authToken="token"
          :recordingFileName="cameraRecordingFileName"
          :brandLogo="cameraBrandLogoPrimary"
          :brandLogoAlt="cameraBrandLogoAlternate"
          :brandLogoHasBadge="cameraBrandLogoHasBadge"
          :competitionName="cameraCompetitionName"
          :athleteName="cameraOverlay.athleteName"
          :athleteCountryName="cameraOverlay.athleteCountryName"
          :athleteCountryCode="cameraOverlay.athleteCountryCode"
          :athleteFlagUrl="cameraOverlay.athleteFlagUrl"
          :stageTitle="cameraOverlay.stageTitle"
          :stageSubtitle="cameraOverlay.stageSubtitle"
          :timer-mode="timerOverlay.mode"
          :timer-value="timerOverlay.value"
          :timer-left-value="timerOverlay.left"
          :timer-right-value="timerOverlay.right"
          :show-timer="timerOverlay.show"
          :vsLeftName="cameraOverlay.vsLeftName"
          :vsLeftFlagUrl="cameraOverlay.vsLeftFlagUrl"
          :vsRightName="cameraOverlay.vsRightName"
          :vsRightFlagUrl="cameraOverlay.vsRightFlagUrl"
          :startTrigger="recordingStartTrigger"
          :canRecord="Boolean(pendingRecordingAthlete)"
          :has-running-timer="isAnyTimerRunning"
          @begin-recording="handleCameraBegin"
          @finish-recording="handleCameraFinish"
          @camera-ready="handleCameraReady"
          @camera-stopped="handleCameraStopped"
          @request-live-toggle="requestLiveServiceToggle"
          @cancel="() => {}"
        >
          <template #actions-extra="{ loading, finalizingRecording }">
            <div class="timer-gear-anchor">
              <button
                class="btn btn-sm btn-outline-secondary"
                :disabled="loading || finalizingRecording || !cameraReady"
                @click="toggleTimerMenu"
              >
                <i class="fa-solid fa-gear"></i>
              </button>
              <div v-if="timerMenuOpen" class="timer-gear-menu">
                <div class="timer-gear-menu__card">
                  <div class="timer-gear-menu__row timer-gear-menu__open">
                    <button
                      class="btn btn-sm btn-outline-primary w-100 d-flex align-items-center justify-content-center gap-2"
                      :disabled="!timerAccessCode"
                      @click="openPublicTimer"
                    >
                      <i class="fa-solid fa-up-right-from-square"></i>
                      <span>{{ $t('judge.timer.open_public') || 'Abrir timer' }}</span>
                    </button>
                  </div>
                  <div class="timer-gear-menu__row timer-gear-menu__open">
                    <button
                      class="btn btn-sm btn-outline-warning w-100 d-flex align-items-center justify-content-center gap-2"
                      @click="clearActiveTimer"
                    >
                      <i class="fa-solid fa-eraser"></i>
                      <span>{{ $t('judge.timer.clear_screen') || 'Limpiar pantalla' }}</span>
                    </button>
                  </div>
                  <div class="timer-gear-menu__row">
                    <div class="timer-theme-selector" role="group" aria-label="Timer theme selector">
                      <button
                        v-for="preset in timerThemeOptions"
                        :key="preset.id"
                        type="button"
                        class="btn btn-sm"
                        :class="timerTheme === preset.id ? 'btn-primary' : 'btn-outline-secondary'"
                        :disabled="!timerAccessCode || timerTheme === preset.id"
                        @click="setTimerTheme(preset.id)"
                      >
                        {{ preset.label }}
                      </button>
                    </div>
                  </div>
                  <div class="timer-gear-menu__row">
                    <label class="timer-gear-menu__label">{{ $t('judge.timer.set_seconds') || 'Tiempo (s)' }}</label>
                    <div class="timer-gear-menu__input">
                      <input
                        type="number"
                        min="30"
                        step="10"
                        v-model.number="timerMenuDuration"
                        :disabled="hasRunningTimerInMenu || !activeTimerControls.length"
                      />
                      <span class="timer-control-card__hint">{{ $t('judge.timer.seconds_suffix') || 's' }}</span>
                      <button
                        class="btn btn-sm btn-primary timer-save-btn"
                        :disabled="hasRunningTimerInMenu || !activeTimerControls.length"
                        @click="applySharedTimerDuration"
                      >
                        <i class="fa-solid fa-floppy-disk"></i>
                        <span>{{ $t('common.save') || 'Guardar' }}</span>
                      </button>
                    </div>
                  </div>
                  <div class="timer-gear-menu__row timer-gear-menu__resets">
                    <span class="timer-gear-menu__label">{{ $t('judge.timer.reset') || 'Resetear' }}</span>
                    <div class="timer-gear-menu__reset-buttons">
                      <template v-if="isVsTimerContext">
                        <button
                          v-for="ctrl in activeTimerControls"
                          :key="`reset-${ctrl.roundId}`"
                          class="btn btn-sm btn-warning timer-reset-btn"
                          :disabled="ctrl.isRunning || ctrl.disableAll || hasRunningTimerInMenu"
                          @click="resetFromMenu(ctrl.roundId)"
                        >
                          <i class="fa-solid fa-arrow-rotate-left"></i>
                          <span class="timer-reset-btn__text">{{ ctrl.label || (ctrl.roundId || '') }}</span>
                        </button>
                      </template>
                      <template v-else>
                        <button
                          class="btn btn-sm btn-warning timer-reset-btn"
                          :disabled="activeTimerControls[0]?.isRunning || activeTimerControls[0]?.disableAll || hasRunningTimerInMenu"
                          @click="resetFromMenu(activeTimerControls[0]?.roundId)"
                        >
                          <i class="fa-solid fa-arrow-rotate-left"></i>
                          <span class="timer-reset-btn__text">{{ activeTimerControls[0]?.label || activeTimerControls[0]?.roundId || '' }}</span>
                        </button>
                      </template>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </CameraModal>
      </div>

      <div
        v-if="isVsTimerContext && activeTimerControls[1]"
        class="timer-control-panel timer-control-panel--compact timer-control-panel--side"
      >
        <div class="timer-control-cards timer-control-cards--stacked">
          <div class="timer-control-card">
            <div class="timer-control-card__header">
              <span class="timer-control-card__label">{{ activeTimerControls[1].label }}</span>
            </div>
            <div class="timer-control-card__pill">{{ activeTimerControls[1].display }}</div>
            <div class="timer-control-card__actions">
              <button
                class="timer-toggle-btn"
                :class="activeTimerControls[1].isRunning ? 'is-running' : 'is-idle'"
                :disabled="activeTimerControls[1].disableAll"
                @click.stop="toggleTimerControl(activeTimerControls[1])"
              >
                <i :class="activeTimerControls[1].isRunning ? 'fa-solid fa-pause' : 'fa-solid fa-play'"></i>
              </button>
            </div>
            <div class="timer-control-card__footer">
              <span class="timer-control-card__state" :class="`state-${activeTimerControls[1].state}`">{{ activeTimerControls[1].stateLabel }}</span>
            </div>
          </div>
        </div>
      </div>

      <div v-if="previewModalOpen" class="recorded-inline-card">
        <div class="recorded-inline-header">
          <h5>{{ $t('judge.camera.preview_title') || 'Vista previa' }}</h5>
        </div>
        <p v-if="recordedVideoName" class="recorded-video-name">{{ recordedVideoName }}</p>
        <LocalVideoPlayer
          v-if="recordedVideoUrl"
          :videoUrl="recordedVideoUrl"
          :upload-disabled="!hasRecordedBlob || isUploadingRecorded"
          :upload-busy="isUploadingRecorded"
          @delete-video-url="handleDeleteRecordedVideo"
          @confirm-video="handleConfirmRecordedVideo"
        />
      </div>

      <div v-if="false && activeTimerControls.length" class="timer-control-panel">
        <div class="timer-control-cards">
          <div
            v-for="ctrl in activeTimerControls"
            :key="ctrl.roundId"
            class="timer-control-card"
          >
            <div class="timer-control-card__header">
              <span class="timer-control-card__label">{{ ctrl.label }}</span>
            </div>
            <div class="timer-control-card__pill">{{ ctrl.display }}</div>
            <div class="timer-control-card__actions">
              <button
                class="timer-toggle-btn"
                :class="ctrl.isRunning ? 'is-running' : 'is-idle'"
                :disabled="ctrl.disableAll"
                @click.stop="toggleTimerControl(ctrl)"
              >
                <i :class="ctrl.isRunning ? 'fa-solid fa-pause' : 'fa-solid fa-play'"></i>
              </button>
            </div>
            <div class="timer-control-card__footer">
              <span class="timer-control-card__state" :class="`state-${ctrl.state}`">{{ ctrl.stateLabel }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="manualUploadModal" class="modal-overlay">
      <div class="modal-card">
        <h5 class="d-flex align-items-center gap-2">
          <span>{{ manualModalTitle }}</span>
          <span class="badge bg-secondary text-uppercase" style="font-size: 10px; letter-spacing: 0.5px;">
            {{ manualUploadMode === 'preview' ? ($t('competitions.preview_manual_badge') || 'Vista previa') : ($t('competitions.upload_manual_badge') || 'Subir') }}
          </span>
        </h5>
        <p class="mb-2">{{ manualUploadAthlete ? manualUploadAthlete.user?.name + ' ' + manualUploadAthlete.user?.surname : '' }}</p>
        <p v-if="manualUploadMode === 'preview'" class="text-muted small">
          {{ $t('competitions.preview_manual_hint') || 'Selecciona un video local para cargarlo en la vista previa sin subirlo aún.' }}
        </p>
        <input
          ref="manualUploadInput"
          type="file"
          accept="video/mp4,video/webm,video/x-matroska,video/avi,video/*"
          class="form-control mb-2"
          :disabled="isUploadingRecorded"
          @change="onManualUploadFileChange"
        />
        <p v-if="manualUploadError" class="text-danger small">{{ manualUploadError }}</p>
        <div class="modal-actions">
          <button class="btn btn-sm btn-secondary" :disabled="isUploadingRecorded" @click="cancelManualUpload">{{ $t('common.cancel') || 'Cancelar' }}</button>
          <button class="btn btn-sm btn-primary" :disabled="!manualUploadFile || isUploadingRecorded" @click="confirmManualUpload">{{ isUploadingRecorded ? ($t('common.uploading') || 'Subiendo...') : manualConfirmLabel }}</button>
        </div>
      </div>
    </div>


    <!-- Botones para seleccionar ronda de calificación -->
    <div v-if="roundsBySection.qualification1.length > 0 && !previewModalOpen" class="round-buttons">
      <button
          v-for="round in [1]"
          :key="'qualify-' + round"
          @click="selectRound(round)"
          class="round-toggle"
          :class="{ active: selectedRoundNumber === round }"
      >
      {{ $t("rounds.qualifying") }} {{ round }}
      </button>

      <button
          v-if="roundsBySection.qualification2.length"
          @click="selectRound(2)"
          class="round-toggle best-result"
          :class="{ active: selectedRoundNumber === 2 }"
      >
        {{ $t("rounds.qualifying") }} 2
      </button>
    </div>

    <!-- Botones para etapas eliminatorias -->
    <div v-if="roundsBySection.eliminations.length > 0 && !previewModalOpen" class="stage-buttons">
      <button
          v-for="entry in roundsBySection.eliminations"
          :key="'stage-' + entry.stageId"
          @click="selectStage(entry.stageId)"
          class="round-toggle stage-toggle"
          :class="{ active: selectedStageName === String(entry.stageId) }"
      >
        {{ getStageName(entry.stageId) }}
      </button>
    </div>

    <div ref="sectionContainer">
      <CompetitionJudgeAssignmentPanel
        v-if="panelMode === 'assignment' && !allowRecording"
        :competition-id="managedCompetitionId"
        :rounds="selectedAssignmentRounds"
        :all-rounds="competitionRounds"
        :judges="eligibleCompetitionJudges"
        :phase-label="selectedAssignmentPhaseLabel"
        :saving-round-ids="assignmentSavingRoundIds"
        :assignment-settings="competitionAssignmentSettings"
        :auth-token="token"
        @assign-round-judge="assignJudgeToRound"
        @skip-round-judge="skipJudgeAssignment"
      />

      <template v-else>
      <!-- Calificación 1 -->
      <template v-if="selectedRoundNumber === 1 && allowRecording">
        <JudgeRoundTable
          :athletes="roundsBySection.qualification1"
          :isVS="competition.is_vs === 1"
          :stageName="null"
          :roundNumber="selectedRoundNumber"
          :initial-table-page="restoredTablePage"
          :initial-table-pair-page="restoredTablePairPage"
          :allowRecording="allowRecording"
          :has-running-timer="isAnyTimerRunning"
          :cameraReady="cameraReady || showRecordedPreview"
          :recordingLocked="Boolean(recordingInProgressId || cameraRecordingStarted)"
          :recorded-athlete-id="recordedAthleteId"
          :recorded-pair-ids="recordedPairIds"
          :preview-modal-open="previewModalOpen"
          :uploadProgress="uploadProgress"
          :isUploadingRecorded="isUploadingRecorded"
          @start-recording="startRecording"
          @stop-recording="stopRecording"
          @score="startScoring"
          @edit-score="editScoring"
          @disqualify="disqualifyAthlete"
          @injury="markInjury"
          @view-score="viewScore"
          @assignment-blocked="showAssignmentBlockedModal"
          @upload-from-card="handleUploadFromCard"
          @preview-from-card="handlePreviewFromCard"
          @delete-from-card="handleDeleteFromCard"
          @update-status="updateStatus"
        />
      </template>

      <JudgeRoundTable
          v-else-if="selectedRoundNumber === 1"
          :athletes="roundsBySection.qualification1"
          :isVS="competition.is_vs === 1"
          :stageName="null"
          :roundNumber="selectedRoundNumber"
          :initial-table-page="restoredTablePage"
          :initial-table-pair-page="restoredTablePairPage"
          :allowRecording="allowRecording"
          :has-running-timer="isAnyTimerRunning"
          :cameraReady="cameraReady"
          :recordingLocked="Boolean(recordingInProgressId || cameraRecordingStarted)"
          :recorded-athlete-id="recordedAthleteId"
          :recorded-pair-ids="recordedPairIds"
          :preview-modal-open="previewModalOpen"
          :uploadProgress="uploadProgress"
            :isUploadingRecorded="isUploadingRecorded"
          @start-recording="startRecording"
          @stop-recording="stopRecording"
          @score="startScoring"
          @edit-score="editScoring"
          @disqualify="disqualifyAthlete"
          @injury="markInjury"
            @view-score="viewScore"
            @assignment-blocked="showAssignmentBlockedModal"
            @upload-from-card="handleUploadFromCard"
            @preview-from-card="handlePreviewFromCard"
            @delete-from-card="handleDeleteFromCard"
            @update-status="updateStatus"
      />

      <!-- Calificación 2 -->
      <JudgeRoundTable
          v-if="selectedRoundNumber === 2"
          :athletes="roundsBySection.qualification2"
          :isVS="competition.is_vs === 1"
          :stageName="null"
          :roundNumber="selectedRoundNumber"
          :initial-table-page="restoredTablePage"
          :initial-table-pair-page="restoredTablePairPage"
          :allowRecording="allowRecording"
          :has-running-timer="isAnyTimerRunning"
          :cameraReady="cameraReady"
          :recordingLocked="Boolean(recordingInProgressId || cameraRecordingStarted)"
          :recorded-athlete-id="recordedAthleteId"
          :recorded-pair-ids="recordedPairIds"
          :preview-modal-open="previewModalOpen"
          :uploadProgress="uploadProgress"
          :isUploadingRecorded="isUploadingRecorded"
          @start-recording="startRecording"
          @stop-recording="stopRecording"
          @score="startScoring"
          @edit-score="editScoring"
          @disqualify="disqualifyAthlete"
          @injury="markInjury"
            @view-score="viewScore"
          @assignment-blocked="showAssignmentBlockedModal"
          @upload-from-card="handleUploadFromCard"
          @preview-from-card="handlePreviewFromCard"
            @delete-from-card="handleDeleteFromCard"
          @update-status="updateStatus"
      />
    <!-- Etapas eliminatorias -->
      <template v-for="entry in roundsBySection.eliminations">
        <JudgeRoundTable
          v-if="selectedStageName === String(entry.stageId)"
            :key="entry.stageId"
          :stageName="String(entry.stageId)"
          :roundNumber="0"
            :initial-table-page="restoredTablePage"
            :initial-table-pair-page="restoredTablePairPage"
            :athletes="entry.rounds"
            :isVS="competition.is_vs === 1"
            :allowRecording="allowRecording"
            :has-running-timer="isAnyTimerRunning"
            :cameraReady="cameraReady"
            :recordingLocked="Boolean(recordingInProgressId || cameraRecordingStarted)"
            :recorded-athlete-id="recordedAthleteId"
            :recorded-pair-ids="recordedPairIds"
            :preview-modal-open="previewModalOpen"
            :uploadProgress="uploadProgress"
            :isUploadingRecorded="isUploadingRecorded"
            @start-recording="startRecording"
            @stop-recording="stopRecording"
            @score="startScoring"
            @edit-score="editScoring"
            @disqualify="disqualifyAthlete"
            @injury="markInjury"
            @view-score="viewScore"
            @assignment-blocked="showAssignmentBlockedModal"
            @upload-from-card="handleUploadFromCard"
            @preview-from-card="handlePreviewFromCard"
            @delete-from-card="handleDeleteFromCard"
            @update-status="updateStatus"
        />
      </template>
      </template>
    </div>
    <ConfirmationModal
        ref="confirmationModal"
        :message="modalMessage"
        :showCancel="showCancel"
        @confirmed="confirmModal"
        @canceled="cancelModal"
    />
  </div>
</template>
<script>
import JudgeRoundTable from "@/components/competitions/JudgeRoundTable.vue";
import CompetitionJudgeAssignmentPanel from "@/components/competitions/CompetitionJudgeAssignmentPanel.vue";
import { mapActions, mapState } from "vuex";
import ConfirmationModal from "@/components/competitions/ConfirmationModal.vue";
import { isOwnerOrganizer, isSupervisor } from "@/helpers/roleUtils";
import { assembleVsBracketMatches } from "@/helpers/vsBracketAssembly";
import CameraModal from "@/components/recording/CameraModal.vue";
import LocalVideoPlayer from "@/services/LocalVideoPlayer.vue";
import echoService from "@/services/echoService";
import { resolveLocalFlagAsset } from "@/lib/flagAssets";
import {
  TIMER_THEME_OPTIONS,
  isAllowedTimerTheme,
  normalizeTimerTheme,
} from "@/constants/timerThemes";

export default {
  name: "CompetitionJudge",
  components: {
    ConfirmationModal,
    JudgeRoundTable,
    CompetitionJudgeAssignmentPanel,
    CameraModal,
    LocalVideoPlayer
  },
  props: {
    competition: {
      type: Object,
      required: true
    },
    allowRecording: {
      type: Boolean,
      default: true,
    }
  },
  data() {
    return {
      defaultAvatar: require("@/assets/user.png"),
      recordingInProgressId: null,
      panelMode: 'operations',
      selectedRoundNumber: null,
      selectedStageName: null,
      showCancel: false,
      modalMessage: "",
      pendingAction: null,
      pendingId: null,
      pendingPayload: null,
      youtubeStatusMessage: "",
      youtubePlaylistId: "",
      youtubeIssues: [],
      youtubePreparing: false,
      imageIssues: [],
      imageUploading: false,
      imageUploadedAt: null,
      youtubeEnabled: false,
      showYoutubeConfirm: false,
      showCameraModal: false,
      showRecordedPreview: false,
      previewModalOpen: false,
      manualUploadModal: false,
      manualUploadAthlete: null,
      manualUploadFile: null,
      manualUploadError: '',
      manualUploadMode: 'upload',
      manualUploadPair: [],
      recordedVideoUrl: '',
      recordedVideoName: '',
      recordedAthleteId: null,
      recordedBlob: null,
      cameraRecordingFileName: '',
      pendingRecordingAthlete: null,
      pendingRecordingPair: [],
      cameraRecordingStarted: false,
      cameraReady: false,
      recordingStartTrigger: 0,
      cameraBrandLogoPrimary: require("@/assets/logos/logo_pill_white.png"),
      cameraBrandLogoAlternate: require("@/assets/logos/logo_pill_black.png"),
      cameraBrandLogoHasBadge: true,
      cameraOverlay: {
        athleteName: '',
        athleteCountryName: '',
        athleteCountryCode: '',
        athleteFlagUrl: '',
        stageTitle: '',
        stageSubtitle: '',
        vsLeftName: '',
        vsLeftFlagUrl: '',
        vsRightName: '',
        vsRightFlagUrl: '',
      },
      countryMetaCache: {},
      uploadProgress: {},
      CHUNK_SIZE: 10 * 1024 * 1024,
      isUploadingRecorded: false,
      pendingResumeCheck: false,
      recordedPairIds: [],
      restoredTablePage: 1,
      restoredTablePairPage: 1,
      judgePresenceChannel: null,
      judgePresenceHydrated: false,
      judgePresenceStates: {},
      uploadChannel: null,
      uploadChannelName: '',
      queuedUploadBroadcasts: {},
      uploadBroadcastFlushHandle: null,
      timerChannel: null,
      timerChannelName: '',
      timerStateByRound: {},
      timerDurationInputs: {},
      timerTickHandle: null,
      timerTickNowMs: Date.now(),
      timerServerDriftMs: 0,
      operationAssignmentTimers: {},
      operationAssignmentTickHandle: null,
      operationAssignmentNowMs: Date.now(),
      timerMenuOpen: false,
      timerMenuDuration: 60,
      timerTheme: 'dark',
      liveChannelState: null,
      liveRoundViewerActive: false,
      liveServiceBusy: false,
      liveChannelRequestId: 0,
      subscribedLiveChannelCode: '',
      assignmentSavingRoundIds: [],
    };
  },
  computed: {
    ...mapState("competition", ["appNavigation", "competitionData", "competitionIdToView"]),
    ...mapState('round', ['competitionRounds']),
    ...mapState('user', ['user', 'token']),

    managedCompetitionId() {
      return this.competition?.id || this.competitionData?.id || this.competitionIdToView || null;
    },

    assignmentCompetitionContext() {
      const propCompetition = this.competition || null;
      const storeCompetition = this.competitionData && typeof this.competitionData === 'object'
        ? this.competitionData
        : null;

      const managedCompetitionId = Number(this.managedCompetitionId || 0);
      const propCompetitionId = Number(propCompetition?.id || 0);
      const storeCompetitionId = Number(storeCompetition?.id || 0);

      if (storeCompetition && managedCompetitionId > 0 && storeCompetitionId === managedCompetitionId) {
        return storeCompetition;
      }

      if (propCompetition && managedCompetitionId > 0 && propCompetitionId === managedCompetitionId) {
        return propCompetition;
      }

      return propCompetition || storeCompetition || null;
    },

    canManageAssignments() {
      const competition = this.assignmentCompetitionContext;
      if (!competition) return false;

      return isSupervisor(this.user) || isOwnerOrganizer(this.user, competition);
    },

    eligibleCompetitionJudges() {
      const competitionUsers = Array.isArray(this.assignmentCompetitionContext?.competition_users)
        ? this.assignmentCompetitionContext.competition_users
        : [];

      return competitionUsers
        .filter((entry) => Number(entry?.role_id ?? entry?.pivot?.role_id) === 2)
        .filter((entry) => Number(entry?.status_id ?? entry?.status?.id ?? entry?.pivot?.status_id) === 4)
        .map((entry, index) => {
          const judge = entry?.user || {};
          const label = [judge?.name, judge?.surname].filter(Boolean).join(' ').trim() || `Judge #${judge?.id || entry?.user_id}`;
          const judgeId = judge?.id || entry?.user_id;
          const presence = this.judgePresenceStates[String(judgeId)] || null;

          return {
            id: judgeId,
            label,
            name: judge?.name || '',
            surname: judge?.surname || '',
            queueOrder: index,
            isOnline: presence
              ? Boolean(presence.is_online)
              : (this.judgePresenceHydrated ? false : Boolean(judge?.is_online)),
            lastSeenAt: presence?.last_seen_at ?? judge?.last_seen_at ?? null,
            techOperatorActive: Boolean(entry?.tech_operator_active),
            techOperatorQualified: Boolean(entry?.tech_operator_qualified),
          };
        })
        .filter((judge) => Boolean(judge.id))
        .sort((left, right) => {
          if (left.queueOrder !== right.queueOrder) {
            return left.queueOrder - right.queueOrder;
          }

          return left.label.localeCompare(right.label);
        });
    },

    athleteCompetitionOrderMap() {
      const competitionUsers = Array.isArray(this.assignmentCompetitionContext?.competition_users)
        ? this.assignmentCompetitionContext.competition_users
        : [];

      return competitionUsers.reduce((map, entry) => {
        const roleId = Number(entry?.role_id ?? entry?.pivot?.role_id ?? 0);
        if (roleId !== 1) return map;

        const athleteUserId = Number(entry?.user_id ?? entry?.user?.id ?? 0);
        const orderNumber = Number(entry?.order_number ?? entry?.pivot?.order_number);

        if (athleteUserId > 0 && Number.isFinite(orderNumber)) {
          map[athleteUserId] = orderNumber;
        }

        return map;
      }, {});
    },

    selectedAssignmentRounds() {
      const rounds = this.getRoundsForCurrentSelection();
      if (this.shouldUseVsOperationOrdering(rounds)) {
        return this.sortVsRoundsForOperations(rounds);
      }

      return this.sortRoundsForDisplay(rounds, this.getCurrentSelectionSortContext());
    },

    competitionAssignmentSettings() {
      const settings = this.assignmentCompetitionContext?.assignment_settings || {};

      return {
        assignment_mode: settings.assignment_mode || this.assignmentCompetitionContext?.assignment_mode || 'automatic',
        tech_operator_release_mode: settings.tech_operator_release_mode || this.assignmentCompetitionContext?.tech_operator_release_mode || 'after_uploads',
      };
    },

    judgeDirectoryById() {
      return this.eligibleCompetitionJudges.reduce((map, judge) => {
        map[String(judge.id)] = judge;
        return map;
      }, {});
    },

    selectedAssignmentPhaseLabel() {
      if (this.selectedRoundNumber === 1) {
        return `${this.translateOr('rounds.qualifying', 'Calificacion')} 1`;
      }

      if (this.selectedRoundNumber === 2) {
        return `${this.translateOr('rounds.qualifying', 'Calificacion')} 2`;
      }

      if (this.selectedStageName !== null && this.selectedStageName !== undefined) {
        return this.getStageName(this.selectedStageName) || `Etapa ${this.selectedStageName}`;
      }

      return 'Fase actual';
    },

    operationTrackedStatuses() {
      return new Set([6, 16, 17, 20, 21, 22]);
    },

    selectedOperationRounds() {
      const rounds = this.getRoundsForCurrentSelection();
      if (this.shouldUseVsOperationOrdering(rounds)) {
        return this.sortVsRoundsForOperations(rounds);
      }

      return this.sortRoundsForDisplay(rounds, this.getCurrentSelectionSortContext());
    },

    selectedPhaseHasPendingVideoWorkflow() {
      const videoPendingStatuses = new Set([13, 16, 17, 20]);
      return this.selectedOperationRounds.some((round) => videoPendingStatuses.has(Number(round?.status_id || 0)));
    },

    operationQueueRounds() {
      const visibleStatuses = new Set([13, 6, 16, 17, 20, 21, 22]);
      return this.selectedOperationRounds.filter((round) => visibleStatuses.has(Number(round?.status_id)));
    },

    activeOperationRound() {
      return this.operationQueueRounds.find((round) => this.operationTrackedStatuses.has(Number(round?.status_id))) || null;
    },

    activeOperationRoundIds() {
      const activeRound = this.activeOperationRound;
      if (!activeRound) {
        return new Set();
      }

      const ids = new Set([String(activeRound.id || '')]);
      const activeMatchKey = this.resolveVsOperationMatchKey(activeRound);
      if (!activeMatchKey) {
        return ids;
      }

      (this.operationQueueRounds || []).forEach((round) => {
        if (!this.operationTrackedStatuses.has(Number(round?.status_id || 0))) {
          return;
        }

        if (this.resolveVsOperationMatchKey(round) !== activeMatchKey) {
          return;
        }

        ids.add(String(round?.id || ''));
      });

      return ids;
    },

    nextOperationRound() {
      return this.operationQueueRounds.find((round) => Number(round?.status_id) === 13) || null;
    },

    showJudgeOperationsAssignmentBanner() {
      return !this.allowRecording && this.panelMode === 'operations' && this.operationQueueRounds.length > 0;
    },

    operationAssignmentCards() {
      const activeRoundIds = this.activeOperationRoundIds;
      const nextRoundId = String(this.nextOperationRound?.id || '');

      return this.operationQueueRounds
        .map((round, index) => {
        const roundId = String(round?.id || '');
        const isCurrent = roundId !== '' && activeRoundIds.has(roundId);
        const isNext = !isCurrent && roundId !== '' && roundId === nextRoundId;
        const isAssigned = Number(this.resolvePersistedOperationalJudgeId(round) || 0) > 0;
        const assignmentCountdown = this.getOperationAssignmentCountdown(round);

        return {
          id: round?.id || index,
          round,
          title: this.formatRoundBannerTitle(round),
          subtitle: this.formatOperationRoundSubtitle(round),
          judgeLabel: this.formatRoundAssignedJudge(round),
          statusLabel: this.getStatusLabel(round?.status_id),
          priorityLabel: isCurrent
            ? this.translateOr('judge.operations.current', 'En curso')
            : (isNext
              ? this.translateOr('judge.operations.next', 'Siguiente')
              : (isAssigned
                ? this.translateOr('judge.operations.assigned', 'Asignada')
                : this.translateOr('judge.operations.unassigned', 'Sin asignar'))),
          tone: isCurrent ? 'current' : (isNext ? 'next' : 'queued'),
          position: index + 1,
          queuePosition: index + 1,
          isAssigned,
          isCurrent,
          isNext,
          canOpenScore: this.canOpenScoreFromOperations(round),
          assignmentCountdown,
        };
      })
        .sort((left, right) => {
          const leftWeight = this.getOperationCardDisplayWeight(left);
          const rightWeight = this.getOperationCardDisplayWeight(right);

          if (leftWeight !== rightWeight) {
            return leftWeight - rightWeight;
          }

          return Number(left.queuePosition || 0) - Number(right.queuePosition || 0);
        });
    },

    judgeOperationsBannerSummary() {
      if (this.operationAssignmentCards.length > 1) {
        return this.translateOr('judge.operations.summary_multiple', 'Desliza para ver quien tiene la ronda en curso, cual es la siguiente y como queda la cola asignada en esta fase.');
      }

      if (this.operationAssignmentCards.length === 1) {
        return this.translateOr('judge.operations.summary_single', 'Solo queda una ronda activa o pendiente en esta fase.');
      }

      return '';
    },

    canSeeYouTubeBar() {
      return isSupervisor(this.user);
    },

    liveToggleTooltip() {
      return this.liveRoundViewerActive ? 'Desactivar live' : 'Activar live';
    },

    competitionPlaylist() {
      return (
        this.competitionData?.competitionPlaylist ||
        this.competitionData?.competition_playlist ||
        this.competition?.competitionPlaylist ||
        this.competition?.competition_playlist ||
        null
      );
    },

    hasPlaylist() {
      return Boolean(this.youtubePlaylistId || this.competitionPlaylist?.youtube_playlist_id);
    },

    youtubeReady() {
      return this.hasPlaylist && Boolean(this.imageUploadedAt);
    },

    roundsBySection() {
      const result = {
        qualification1: [],
        qualification2: [],
        eliminations: [],
      };

      if (!this.competitionRounds || !this.competition) return result;

      const roundSizes = this.competition.round_sizes || [];
      const tempEliminations = {};

      this.competitionRounds.forEach((round) => {
        const decorated = this.decorateRoundWithFeedback(round);

        if (round.round_number === 1) {
          result.qualification1.push(decorated);
        } else if (round.round_number === 2 && this.competition.is_qualification_dual) {
          result.qualification2.push(decorated);
        } else if (round.round_size_id) {
          const size = roundSizes.find((s) => s.id === round.round_size_id);
          const stage = size ? size.id : `Stage ${round.round_size_id}`;

          if (!tempEliminations[stage]) tempEliminations[stage] = [];
          tempEliminations[stage].push(decorated);
        }
      });

      const orderedStages = Object.keys(tempEliminations)
        .map((stageId) => parseInt(stageId, 10))
        .sort((a, b) => b - a);

      result.qualification1 = this.sortRoundsForDisplay(result.qualification1, { phase: 'qualification1' });
      result.qualification2 = this.sortRoundsForDisplay(result.qualification2, { phase: 'qualification2' });

      result.eliminations = orderedStages.map((stageId) => ({
        stageId,
        rounds: this.sortRoundsForDisplay(tempEliminations[stageId], { phase: 'elimination', stageId }),
      }));

      return result;
    },

    stages() {
      return this.competition.round_sizes
        ? [...this.competition.round_sizes].sort((a, b) => b.id - a.id)
        : [];
    },

    cameraCompetitionName() {
      return (
        this.competitionData?.name ||
        this.competition?.name ||
        ''
      );
    },

    timerAccessCode() {
      return this.competition?.timer_access_code || this.competition?.timerAccessCode || null;
    },

    runningRoundIds() {
      return Object.entries(this.timerStateByRound || {})
        .filter(([, state]) => (state?.state || '').toLowerCase() === 'running')
        .map(([roundId]) => String(roundId));
    },

    hasRunningTimerInMenu() {
      return this.activeTimerControls.some((ctrl) => ctrl.isRunning);
    },

    isAnyTimerRunning() {
      return this.runningRoundIds.length > 0;
    },

    activeRunningRoundId() {
      return this.runningRoundIds[0] || null;
    },

    timerThemeOptions() {
      return TIMER_THEME_OPTIONS;
    },

    cameraPlaylistId() {
      const playlist = this.competitionPlaylist;
      return (
        // Prefer internal numeric id if present, then fallback to legacy/youtube ids
        playlist?.id ||
        playlist?.playlist_id ||
        this.competition?.playlist_id ||
        this.competition?.playlistId ||
        this.youtubePlaylistId ||
        playlist?.youtube_playlist_id ||
        null
      );
    },

    cameraWorkflowEnabled() {
      return Boolean(this.cameraPlaylistId);
    },

    activeRecordingTargets() {
      const pair = Array.isArray(this.pendingRecordingPair) ? this.pendingRecordingPair.filter(Boolean) : [];
      if (pair.length) return pair;
      return this.pendingRecordingAthlete ? [this.pendingRecordingAthlete] : [];
    },

    timerOverlay() {
      const targets = this.activeRecordingTargets;
      if (!targets.length) return { mode: 'single', value: '', left: '', right: '', show: false };

      const first = this.timerDisplayForAthlete(targets[0]);
      if (targets.length > 1) {
        const second = this.timerDisplayForAthlete(targets[1]);
        return {
          mode: 'vs',
          left: first,
          right: second,
          value: '',
          show: Boolean(first || second),
        };
      }

      return {
        mode: 'single',
        value: first,
        left: '',
        right: '',
        show: Boolean(first),
      };
    },

    activeTimerControls() {
      const targets = this.activeRecordingTargets;
      if (!targets.length) return [];
      const isVsContext = this.isVsPayload(targets);
      return targets.map((athlete, index) => {
        const roundId = this.resolveRoundIdForAthlete(athlete?.id) || athlete?.round_id || athlete?.id;
        const state = this.timerStateByRound[roundId] || {};
        const label = athlete?.user ? `${athlete.user.name || ''} ${athlete.user.surname || ''}`.trim() || (athlete?.name || '') : (athlete?.name || '');
        const isRunning = (state.state || '').toLowerCase() === 'running';
        // Solo bloquea el otro control cuando hay contexto VS; en modo simple no se deshabilita
        const disableAll = isVsContext && this.isAnyTimerRunning && String(this.activeRunningRoundId) !== String(roundId);
        return {
          roundId,
          label: label || (index === 0 ? this.$t('rounds.left') || 'Izquierda' : this.$t('rounds.right') || 'Derecha'),
          display: this.formatTimerValue(this.getEffectiveTimerDisplaySeconds(state)),
          state: state.state || 'idle',
            stateLabel: this.getTimerStateLabel(state.state),
          editValue: this.timerDurationInputs[roundId] ?? state.duration_seconds ?? state.duration ?? 0,
          isRunning,
          disableAll,
        };
      }).filter(ctrl => ctrl.roundId);
    },

    isVsTimerContext() {
      const targets = this.activeRecordingTargets;
      return this.isVsPayload(targets);
    },

    trackedTimerRoundIds() {
      return this.activeRecordingTargets
        .map((athlete) => this.resolveRoundIdForAthlete(athlete?.id) || athlete?.round_id || athlete?.id)
        .filter(Boolean)
        .map((roundId) => String(roundId));
    },

    shouldTrackLiveTimers() {
      return Boolean(
        this.timerAccessCode
        && (
          this.trackedTimerRoundIds.length
          || this.cameraRecordingStarted
          || this.recordingInProgressId
          || this.previewModalOpen
          || this.timerMenuOpen
        )
      );
    },

    hasRunningTrackedTimer() {
      return this.trackedTimerRoundIds.some((roundId) => {
        const state = String(this.timerStateByRound?.[roundId]?.state || '').toLowerCase();
        return state === 'running';
      });
    },

    hasRecordedBlob() {
      // Enable upload when we have a blob and a target athlete; playlist validation happens inside uploadRecordedVideo
      return Boolean(this.recordedBlob && this.recordedAthleteId);
    },

    manualConfirmLabel() {
      if (this.manualUploadMode === 'preview') {
        return this.$t('competitions.preview_manual_confirm') || 'Cargar vista previa';
      }
      return this.$t('competitions.upload_manual_confirm') || 'Subir';
    },

    manualModalTitle() {
      if (this.manualUploadMode === 'preview') {
        return this.$t('competitions.preview_manual_title') || 'Cargar video para vista previa';
      }
      return this.$t('competitions.upload_manual_title') || 'Subir video';
    },
  },
  watch: {
    competition: {
      deep: true,
      immediate: true,
      handler() {
        this.syncTimerThemeFromCompetition();
      },
    },
    hasPlaylist(val) {
      if (val) {
        this.youtubeEnabled = true;
        this.subscribeToPlaylistUpdates();
      }
    },
    competitionIdToView() {
      this.subscribeToPlaylistUpdates();
      this.subscribeToTimerUpdates();
    },
    managedCompetitionId: {
      immediate: true,
      handler() {
        this.loadLiveChannelState();
      },
    },
    selectedRoundNumber(val) {
      if (val) this.scrollToSection();
    },
    selectedStageName(val) {
      if (val) this.scrollToSection();
    },
    competitionRounds: {
      deep: true,
      immediate: true,
      handler() {
        this.hydrateUploadedProgressFromRounds();
        if (!this.shouldTrackLiveTimers) {
          this.hydrateTimersFromRounds();
        }
        this.ensureDefaultSelection();
        this.syncOperationAssignmentTimers();
        this.refreshOperationAssignmentTicker();
      },
    },
    judgePresenceStates: {
      deep: true,
      handler() {
        this.syncOperationAssignmentTimers();
        this.refreshOperationAssignmentTicker();
      },
    },
    assignmentCompetitionContext() {
      if (!this.judgePresenceHydrated) {
        return;
      }

      this.reconcileJudgePresenceRoster();
    },
    timerAccessCode() {
      this.subscribeToTimerUpdates();
    },
    shouldTrackLiveTimers() {
      this.subscribeToTimerUpdates();
      this.refreshTimerTicker();
    },
    trackedTimerRoundIds() {
      this.subscribeToTimerUpdates();
      this.refreshTimerTicker();
    },
    hasRunningTrackedTimer: {
      immediate: true,
      handler() {
        this.refreshTimerTicker();
      },
    },
  },
  mounted() {
    if (this.appNavigation.competition?.cameFromJudgingManual) {
      this.selectedRoundNumber = this.appNavigation.competition?.roundNumber ?? null;
      this.selectedStageName = this.normalizeStageName(this.appNavigation.competition?.stageName ?? null);
      this.restoredTablePage = this.normalizeRestoredPage(this.appNavigation.competition?.judgeTablePage);
      this.restoredTablePairPage = this.normalizeRestoredPage(this.appNavigation.competition?.judgeTablePairPage);
      this.clearAppNavigationZone("competition");
    }

    const playlist = this.competitionPlaylist;
    if (playlist?.youtube_playlist_id) {
      this.youtubePlaylistId = playlist.youtube_playlist_id;
      this.imageUploadedAt = playlist.imagen_upload_at || playlist.image_uploaded_at || null;
      this.youtubeStatusMessage = this.$t('competitions.youtube.playlist_created');
      this.youtubeEnabled = true;
    }

    if (this.allowRecording && this.cameraWorkflowEnabled) {

        this.ensureDefaultSelection();
      this.showCameraModal = true;
    }

    this.hydrateStoredUploadProgress();
    this.setupJudgePresenceChannel();
    this.syncOperationAssignmentTimers();
    this.refreshOperationAssignmentTicker();
    this.subscribeToPlaylistUpdates();
    this.subscribeToTimerUpdates();
    this.refreshTimerTicker();
  },
  beforeUnmount() {
    this.teardownJudgePresenceChannel();
    this.unsubscribeFromPlaylistUpdates();
    this.unsubscribeFromTimerUpdates();
    this.unsubscribeFromLiveChannel();
    this.stopOperationAssignmentTicker();
    this.stopTimerTicker();
  },
  methods: {
    ...mapActions("competition", ["updateAppNavigationZone", "clearAppNavigationZone"]),
    ...mapActions("round", ["upsertRound"]),

    translateOr(key, fallback) {
      const translated = typeof this.$t === 'function' ? this.$t(key) : '';
      return translated && translated !== key ? translated : fallback;
    },

    normalizeVsPairPayload(pair = null) {
      const list = Array.isArray(pair) ? pair.filter(Boolean) : [];
      return this.isVsPayload(list) ? list : [];
    },

    isVsPayload(targets = []) {
      const list = Array.isArray(targets) ? targets.filter(Boolean) : [];
      if (list.length < 2 || Number(this.competition?.is_vs || 0) !== 1) {
        return false;
      }

      const roundSizeIds = [...new Set(list.map((target) => Number(target?.round_size_id || 0)).filter((value) => value > 0))];
      if (roundSizeIds.length !== 1) {
        return false;
      }

      const matchKeys = [...new Set(list.map((target) => this.resolveVsPayloadMatchKey(target)).filter(Boolean))];
      return matchKeys.length === 1;
    },

    resolveVsPayloadMatchKey(target = null) {
      if (!target) {
        return null;
      }

      const bracket = target?.vs_bracket || null;
      const explicitKey = String(bracket?.match_key || '').trim();
      if (explicitKey) {
        return explicitKey;
      }

      const roundSizeId = Number(target?.round_size_id || 0);
      const group = String(bracket?.group || target?.vs_group || '').trim();
      const matchNumber = Number(bracket?.match_number ?? target?.vs_match_number ?? 0);
      if (!roundSizeId || (!group && !matchNumber)) {
        return null;
      }

      return `${roundSizeId}:${group}:${matchNumber}`;
    },

    getOperationCardDisplayWeight(card = {}) {
      if (card?.isCurrent) {
        return 0;
      }

      if (card?.isAssigned) {
        return 1;
      }

      if (card?.isNext) {
        return 2;
      }

      return 3;
    },

    resolveVsOperationMatchKey(round = null) {
      if (Number(this.competition?.is_vs || 0) !== 1) {
        return null;
      }

      if (!Number(round?.round_size_id || 0)) {
        return null;
      }

      const bracket = round?.vs_bracket || null;
      const explicitKey = String(bracket?.match_key || '').trim();
      if (explicitKey) {
        return explicitKey;
      }

      const group = String(bracket?.group || round?.vs_group || '').trim();
      const matchNumber = Number(bracket?.match_number ?? round?.vs_match_number ?? 0);
      if (!group && !matchNumber) {
        return null;
      }

      return `${Number(round?.round_size_id || 0)}:${group}:${matchNumber}`;
    },

    resolveAssignmentTimerStartedAt(round = null) {
      const candidates = [
        round?.assignment_timer_started_at,
        round?.judge_assigned_at,
        round?.updated_at,
        round?.created_at,
      ];

      for (const candidate of candidates) {
        const parsed = Date.parse(candidate);
        if (Number.isFinite(parsed)) {
          return parsed;
        }
      }

      return Date.now();
    },

    isOperationAssignmentTimedRound(round = null) {
      if (Number(round?.status_id || 0) !== 21) return false;
      return Number(this.resolvePersistedOperationalJudgeId(round) || 0) > 0;
    },

    resolvePersistedOperationalJudgeId(round = null) {
      const judgeId = Number(round?.judge_id || 0);
      return judgeId > 0 ? judgeId : null;
    },

    resolveOperationalJudgeId(round = null) {
      const judgeId = this.resolvePersistedOperationalJudgeId(round);
      if (!judgeId) {
        return null;
      }

      const releasableStatuses = new Set([13, 16, 17, 20, 21]);
      if (!releasableStatuses.has(Number(round?.status_id || 0))) {
        return judgeId;
      }

      return this.isJudgeAvailableForOperations(judgeId) ? judgeId : null;
    },

    isJudgeAvailableForOperations(judgeId) {
      const normalizedJudgeId = Number(judgeId || 0);
      if (!normalizedJudgeId) {
        return true;
      }

      const judge = this.judgeDirectoryById[String(normalizedJudgeId)] || null;
      if (!judge?.isOnline) {
        return false;
      }

      return !this.isTechOperatorHeldForCurrentPhase(judge);
    },

    isTechOperatorHeldForCurrentPhase(judge = null) {
      if (!judge?.techOperatorActive) {
        return false;
      }

      return this.competitionAssignmentSettings.tech_operator_release_mode === 'after_uploads'
        && this.selectedPhaseHasPendingVideoWorkflow;
    },

    syncOperationAssignmentTimers() {
      const next = {};

      (this.operationQueueRounds || []).forEach((round) => {
        if (!this.isOperationAssignmentTimedRound(round)) {
          return;
        }

        const roundId = String(round?.id || '');
        const judgeId = Number(round?.judge_id || 0);

        next[roundId] = {
          judgeId,
          startedAt: this.resolveAssignmentTimerStartedAt(round),
        };
      });

      this.operationAssignmentTimers = next;
    },

    startOperationAssignmentTicker() {
      if (this.operationAssignmentTickHandle) {
        return;
      }

      this.operationAssignmentTickHandle = setInterval(() => {
        this.operationAssignmentNowMs = Date.now();
      }, 1000);
    },

    stopOperationAssignmentTicker() {
      if (!this.operationAssignmentTickHandle) {
        return;
      }

      clearInterval(this.operationAssignmentTickHandle);
      this.operationAssignmentTickHandle = null;
    },

    refreshOperationAssignmentTicker() {
      if (Object.keys(this.operationAssignmentTimers).length > 0) {
        this.startOperationAssignmentTicker();
        return;
      }

      this.stopOperationAssignmentTicker();
    },

    formatOperationAssignmentCountdown(ms = 0) {
      const totalSeconds = Math.max(0, Math.ceil(Number(ms || 0) / 1000));
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = totalSeconds % 60;
      return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    },

    getOperationAssignmentCountdown(round = null) {
      const roundId = String(round?.id || '');
      const timer = this.operationAssignmentTimers[roundId];
      if (!timer) return '';

      const elapsed = Math.max(0, this.operationAssignmentNowMs - Number(timer.startedAt || this.operationAssignmentNowMs));
      const remaining = Math.max(0, (60 * 1000) - elapsed);
      return this.formatOperationAssignmentCountdown(remaining);
    },

    normalizeJudgePresencePayload(payload = {}, fallbackOnline = true) {
      const userId = payload?.user_id || payload?.id;
      if (!userId) return null;

      return {
        id: userId,
        user_id: userId,
        is_online: typeof payload?.is_online === 'boolean' ? payload.is_online : fallbackOnline,
        last_seen_at: payload?.last_seen_at || null,
      };
    },

    mergeJudgePresence(payload = {}, fallbackOnline = true) {
      const normalized = this.normalizeJudgePresencePayload(payload, fallbackOnline);
      if (!normalized?.id) return;

      this.judgePresenceStates = {
        ...this.judgePresenceStates,
        [String(normalized.id)]: {
          ...(this.judgePresenceStates[String(normalized.id)] || {}),
          ...normalized,
        },
      };
    },

    reconcileJudgePresenceRoster(onlineUsers = null) {
      const nextPresenceStates = {
        ...this.judgePresenceStates,
      };

      if (Array.isArray(onlineUsers)) {
        onlineUsers.forEach((user) => {
          const normalized = this.normalizeJudgePresencePayload(user, true);
          if (!normalized?.id) {
            return;
          }

          nextPresenceStates[String(normalized.id)] = {
            ...(nextPresenceStates[String(normalized.id)] || {}),
            ...normalized,
            is_online: true,
          };
        });
      }

      const trackedJudgeIds = this.eligibleCompetitionJudges
        .map((judge) => Number(judge?.id || 0))
        .filter((judgeId) => judgeId > 0);

      if (!trackedJudgeIds.length) {
        this.judgePresenceStates = nextPresenceStates;
        this.judgePresenceHydrated = true;
        return;
      }

      const onlineMap = new Map();

      if (Array.isArray(onlineUsers)) {
        onlineUsers.forEach((user) => {
          const normalized = this.normalizeJudgePresencePayload(user, true);
          if (normalized?.id) {
            onlineMap.set(String(normalized.id), normalized);
          }
        });
      } else {
        Object.values(this.judgePresenceStates || {}).forEach((presence) => {
          const normalized = this.normalizeJudgePresencePayload(presence, Boolean(presence?.is_online));
          if (normalized?.id && normalized.is_online) {
            onlineMap.set(String(normalized.id), normalized);
          }
        });
      }

      trackedJudgeIds.forEach((judgeId) => {
        const key = String(judgeId);
        const current = nextPresenceStates[key] || {};
        const onlinePresence = onlineMap.get(key);

        nextPresenceStates[key] = onlinePresence
          ? {
            ...current,
            ...onlinePresence,
            is_online: true,
          }
          : {
            ...current,
            id: judgeId,
            user_id: judgeId,
            is_online: false,
            last_seen_at: current?.last_seen_at || null,
          };
      });

      this.judgePresenceStates = nextPresenceStates;
      this.judgePresenceHydrated = true;
    },

    setupJudgePresenceChannel() {
      if (this.judgePresenceChannel) {
        return;
      }

      try {
        if (typeof echoService.refreshAuthHeaders === 'function') {
          echoService.refreshAuthHeaders();
        }

        const channel = echoService.join('presence.online-users');
        const handlePresenceEvent = (eventPayload = {}) => {
          this.mergeJudgePresence(eventPayload, true);
        };

        channel
          .here((users = []) => {
            this.reconcileJudgePresenceRoster(users);
          })
          .joining((user) => {
            this.mergeJudgePresence(user, true);
          })
          .leaving((user) => {
            this.mergeJudgePresence({
              user_id: user?.user_id || user?.id,
              last_seen_at: new Date().toISOString(),
              is_online: false,
            }, false);
          })
          .listen('UserPresenceUpdated', handlePresenceEvent)
          .listen('.UserPresenceUpdated', handlePresenceEvent)
          .listen(String.raw`.App\Events\Users\UserPresenceUpdated`, handlePresenceEvent);

        this.judgePresenceChannel = channel;
      } catch (error) {
        console.warn('[JudgePresence] failed to subscribe', error?.message || error);
      }
    },

    teardownJudgePresenceChannel() {
      if (!this.judgePresenceChannel) {
        return;
      }

      try {
        echoService.leave('presence.online-users');
      } catch (error) {
        console.warn('[JudgePresence] failed to unsubscribe', error?.message || error);
      }

      this.judgePresenceChannel = null;
    },

    formatRoundBannerTitle(round) {
      const athleteName = [round?.user?.name, round?.user?.surname].filter(Boolean).join(' ').trim();
      if (athleteName) return athleteName;
      return `Ronda #${round?.id || '-'}`;
    },

    formatOperationRoundSubtitle(round) {
      const parts = [];

      if (round?.round_size_id) {
        parts.push(this.getStageName(round.round_size_id) || `Etapa ${round.round_size_id}`);
      } else if (round?.round_number) {
        parts.push(`${this.translateOr('rounds.qualifying', 'Calificacion')} ${round.round_number}`);
      }

      if (round?.vs_group && round?.vs_match_number) {
        parts.push(`VS ${round.vs_group}-${round.vs_match_number}`);
      }

      return parts.join(' · ') || this.translateOr('judge.operations.current_phase', 'Fase en curso');
    },

    formatRoundAssignedJudge(round) {
      const judgeId = Number(this.resolvePersistedOperationalJudgeId(round) || 0);
      if (!judgeId) {
        return this.translateOr('judge.operations.no_judge_assigned', 'Sin juez asignado');
      }

      if (judgeId === Number(this.user?.id || 0)) {
        return this.translateOr('judge.operations.assigned_to_you', 'Asignada a ti');
      }

      const judge = this.judgeDirectoryById[String(judgeId)];
      return judge?.label
        ? `${this.translateOr('judge.operations.judge_prefix', 'Juez')}: ${judge.label}`
        : `${this.translateOr('judge.operations.judge_fallback', 'Juez')} #${judgeId}`;
    },

    canOpenScoreFromOperations(round = null) {
      if (!round) return false;
      if (Number(round?.status_id || 0) !== 21) return false;

      const currentUserId = Number(this.user?.id || 0);
      const assignedJudgeId = Number(this.resolvePersistedOperationalJudgeId(round) || 0);

      if (!currentUserId || !assignedJudgeId) {
        return false;
      }

      return assignedJudgeId === currentUserId;
    },

    buildScoreNavigationForRound(round = null) {
      const stageName = round?.round_size_id ? this.normalizeStageName(round.round_size_id) : null;

      return {
        stageName,
        roundNumber: stageName ? 0 : Number(round?.round_number || 0),
        tablePage: 1,
        tablePairPage: 1,
      };
    },

    handleOperationCardClick(card = {}) {
      const round = card?.round || null;
      if (!this.canOpenScoreFromOperations(round)) {
        return;
      }

      this.startScoring({
        athlete: round,
        ...this.buildScoreNavigationForRound(round),
      });
    },

    showAssignmentBlockedModal({ athlete }) {
      const athleteName = [athlete?.user?.name, athlete?.user?.surname].filter(Boolean).join(' ').trim() || 'Esta ronda';
      const assignedJudge = this.formatRoundAssignedJudge(athlete);

      this.modalMessage = `${athleteName} no esta asignada a tu panel. ${assignedJudge}.`;
      this.showCancel = false;
      this.pendingAction = 'assignment-blocked';
      this.pendingId = athlete?.id || null;
      this.$refs.confirmationModal.openModal();
    },

    isRoundAssignmentSaving(roundId) {
      return this.assignmentSavingRoundIds.map(String).includes(String(roundId));
    },

    async assignJudgeToRound({ roundId, judgeId }) {
      if (!roundId || this.isRoundAssignmentSaving(roundId)) return;

      this.assignmentSavingRoundIds = [...this.assignmentSavingRoundIds, roundId];

      try {
        const response = await this.$axios.post(`/rounds/${roundId}/assign-judge`, {
          judge_id: judgeId,
        });

        if (response?.data?.round) {
          await this.upsertRound(response.data.round);
        }
      } catch (error) {
        console.warn('[JudgeAssignment] failed to assign judge', error?.response?.data?.message || error?.message || error);
      } finally {
        this.assignmentSavingRoundIds = this.assignmentSavingRoundIds.filter((id) => String(id) !== String(roundId));
      }
    },

    async skipJudgeAssignment({ roundId }) {
      const judges = (this.eligibleCompetitionJudges || []).filter((judge) => this.isJudgeAvailableForOperations(judge?.id));
      if (!roundId || !judges.length) return;

      const round = (this.competitionRounds || []).find((entry) => String(entry?.id) === String(roundId));
      if (!round) return;

      const currentJudgeIndex = judges.findIndex((judge) => Number(judge.id) === Number(round?.judge_id));
      const nextJudge = currentJudgeIndex === -1
        ? judges[0]
        : judges[(currentJudgeIndex + 1) % judges.length];

      if (!nextJudge) return;

      await this.assignJudgeToRound({
        roundId,
        judgeId: nextJudge.id,
      });
    },

    normalizeRestoredPage(value) {
      const normalized = Number(value);
      return Number.isInteger(normalized) && normalized > 0 ? normalized : 1;
    },

    unwrapRealtimePayload(raw) {
      if (!raw) return raw;

      let next = raw;

      try {
        if (typeof next === 'string') {
          next = JSON.parse(next);
        }

        if (next?.data && typeof next.data === 'string') {
          next = { ...next, ...JSON.parse(next.data) };
        } else if (next?.data && typeof next.data === 'object') {
          next = { ...next, ...next.data };
        }

        if (next?.payload && typeof next.payload === 'object') {
          next = { ...next, ...next.payload };
        }
      } catch (e) {
        console.warn('[Timer] unwrapRealtimePayload failed', e?.message || e);
      }

      return next;
    },

    formatTimerValue(rawSeconds) {
      const total = Number(rawSeconds);
      if (!Number.isFinite(total)) return '00:00';
      const sign = total < 0 ? '-' : '';
      const clamped = Math.abs(Math.floor(total));
      const minutes = Math.floor(clamped / 60).toString().padStart(2, '0');
      const seconds = (clamped % 60).toString().padStart(2, '0');
      return `${sign}${minutes}:${seconds}`;
    },

    getTimerStateLabel(rawState) {
      const key = String(rawState || 'idle').toLowerCase();
      const translationKey = `judge.timer.state_${key}`;
      if (this.$t) return this.$t(translationKey);
      return translationKey;
    },

    timerAuthHeaders() {
      return this.token ? { Authorization: `Bearer ${this.token}` } : {};
    },

    createDefaultRoundViewerService() {
      return {
        service_type: 'round_viewer',
        label: 'Service 3',
        enabled: false,
        status: 'inactive',
        is_primary: false,
        sort_order: 3,
        round_id: null,
        round_images: [],
        config: {
          round_id: null,
          round_images: [],
        },
      };
    },

    normalizeLiveChannelService(service = {}) {
      const enabled = typeof service.enabled === 'boolean'
        ? service.enabled
        : String(service.status || '').toLowerCase() === 'active';

      return {
        ...service,
        enabled,
        status: enabled ? 'active' : 'inactive',
        config: service.config && typeof service.config === 'object'
          ? { ...service.config }
          : {},
        youtube_urls: Array.isArray(service.youtube_urls) ? [...service.youtube_urls] : [],
        round_images: Array.isArray(service.round_images) ? [...service.round_images] : [],
        images: Array.isArray(service.images) ? [...service.images] : [],
      };
    },

    normalizeLiveChannelState(channel = {}) {
      const services = Array.isArray(channel.services)
        ? channel.services.map((service) => this.normalizeLiveChannelService(service))
        : [];

      if (!services.some((service) => service.service_type === 'round_viewer')) {
        services.push(this.createDefaultRoundViewerService());
      }

      return {
        ...channel,
        status: channel.status || 'draft',
        is_online: Boolean(channel.is_online),
        title: channel.title || '',
        short_description: channel.short_description || '',
        operator_message: channel.operator_message || '',
        poster_image: channel.poster_image || '',
        featured_on_home: channel.featured_on_home !== false,
        settings: {
          preferred_language: channel.settings?.preferred_language || null,
          preferred_theme: channel.settings?.preferred_theme || null,
        },
        modules: channel.modules && typeof channel.modules === 'object'
          ? { ...channel.modules }
          : {},
        services,
      };
    },

    getRoundViewerService(channel = this.liveChannelState) {
      return Array.isArray(channel?.services)
        ? channel.services.find((service) => service.service_type === 'round_viewer') || null
        : null;
    },

    isLiveServiceEnabled(service = null) {
      if (!service) return false;
      return Boolean(service.enabled) || String(service.status || '').toLowerCase() === 'active';
    },

    applyLiveChannelState(channel = {}) {
      const normalizedChannel = this.normalizeLiveChannelState(channel);
      const roundViewer = this.getRoundViewerService(normalizedChannel);
      this.liveChannelState = normalizedChannel;
      this.liveRoundViewerActive = this.isLiveServiceEnabled(roundViewer);
      this.subscribeToLiveChannel(normalizedChannel.channel_code || normalizedChannel.channelCode || '');
    },

    subscribeToLiveChannel(channelCode) {
      const nextCode = String(channelCode || '').trim();

      if (!nextCode) {
        this.unsubscribeFromLiveChannel();
        return;
      }

      if (this.subscribedLiveChannelCode === nextCode) {
        return;
      }

      this.unsubscribeFromLiveChannel();
      this.subscribedLiveChannelCode = nextCode;

      try {
        echoService
          .channel(`live-channel.${nextCode}`)
          .listen('.CompetitionLiveChannelUpdated', payload => this.handleLiveChannelUpdated(payload));
      } catch (error) {
        console.warn('[Live] failed to subscribe to live channel updates', error?.message || error);
      }
    },

    unsubscribeFromLiveChannel() {
      if (!this.subscribedLiveChannelCode) {
        return;
      }

      try {
        echoService.leaveChannel(`live-channel.${this.subscribedLiveChannelCode}`);
      } catch (error) {
        console.warn('[Live] failed to unsubscribe from live channel updates', error?.message || error);
      }

      this.subscribedLiveChannelCode = '';
    },

    async handleLiveChannelUpdated(payload) {
      if (!payload || !this.managedCompetitionId) {
        return;
      }

      if (payload.refresh_state) {
        await this.loadLiveChannelState();
        return;
      }

      this.applyLiveChannelState(payload);
    },

    buildLiveServiceConfig(service = {}) {
      return {
        ...(service.config && typeof service.config === 'object' ? service.config : {}),
        message: service.message ?? service.config?.message ?? '',
        youtube_urls: Array.isArray(service.youtube_urls)
          ? service.youtube_urls
          : (Array.isArray(service.config?.youtube_urls) ? service.config.youtube_urls : []),
        youtube_url: service.youtube_url ?? service.config?.youtube_url ?? null,
        youtube_start_video_id: service.youtube_start_video_id ?? service.config?.youtube_start_video_id ?? null,
        youtube_video_id: service.youtube_video_id ?? service.config?.youtube_video_id ?? null,
        youtube_video_url: service.youtube_video_url ?? service.config?.youtube_video_url ?? null,
        youtube_embed_url: service.youtube_embed_url ?? service.config?.youtube_embed_url ?? null,
        youtube_autoplay: Boolean(service.youtube_autoplay ?? service.config?.youtube_autoplay),
        youtube_controls: Boolean(service.youtube_controls ?? service.config?.youtube_controls),
        playback: service.config?.playback || service.playback || null,
        round_id: service.round_id ?? service.config?.round_id ?? null,
        round_images: Array.isArray(service.round_images)
          ? service.round_images
          : (Array.isArray(service.config?.round_images) ? service.config.round_images : []),
        images: Array.isArray(service.images)
          ? service.images
          : (Array.isArray(service.config?.images) ? service.config.images : []),
        interval_seconds: Number(service.interval_seconds ?? service.config?.interval_seconds) || 10,
      };
    },

    buildLiveChannelPayload(channel = this.liveChannelState) {
      const normalizedChannel = this.normalizeLiveChannelState(channel || {});

      return {
        status: 'live',
        is_online: true,
        title: normalizedChannel.title,
        short_description: normalizedChannel.short_description,
        operator_message: normalizedChannel.operator_message,
        poster_image: normalizedChannel.poster_image,
        featured_on_home: normalizedChannel.featured_on_home,
        settings: {
          preferred_language: normalizedChannel.settings?.preferred_language || null,
          preferred_theme: normalizedChannel.settings?.preferred_theme || null,
        },
        services: normalizedChannel.services.map((service, index) => ({
          service_type: service.service_type,
          label: service.label,
          status: this.isLiveServiceEnabled(service) ? 'active' : 'inactive',
          is_primary: Boolean(service.is_primary),
          sort_order: Number(service.sort_order) || index + 1,
          config: this.buildLiveServiceConfig(service),
        })),
        modules: {
          ...(normalizedChannel.modules || {}),
        },
      };
    },

    async loadLiveChannelState() {
      const competitionId = this.managedCompetitionId;
      if (!this.allowRecording || !competitionId) {
        this.unsubscribeFromLiveChannel();
        this.liveChannelState = null;
        this.liveRoundViewerActive = false;
        return;
      }

      const requestId = ++this.liveChannelRequestId;

      try {
        const response = await this.$axios.get(`/competitions/${competitionId}/live-channel`);
        if (requestId !== this.liveChannelRequestId) return;
        this.applyLiveChannelState(response.data?.channel || {});
      } catch (error) {
        if (requestId !== this.liveChannelRequestId) return;
        this.liveChannelState = null;
        this.liveRoundViewerActive = false;
        console.warn('[Live] failed to load live channel state', error?.response?.data?.message || error?.message || error);
      }
    },

    requestLiveServiceToggle() {
      if (this.liveServiceBusy || this.cameraRecordingStarted || this.recordingInProgressId) return;

      const nextActive = !this.liveRoundViewerActive;
      const message = nextActive
        ? 'Quieres activar la transmision al Live?'
        : 'Quieres desactivar la transmision al Live?';

      this.controlPop('toggle-live-service', nextActive, message);
    },

    liveFocusableStatuses() {
      return new Set([6, 13, 16, 17, 19, 20, 21, 22, 23]);
    },

    isRoundFocusableForLive(round = null) {
      const statusId = Number(round?.status_id);
      return this.liveFocusableStatuses().has(statusId);
    },

    sortRoundsNewestFirst(rounds = []) {
      return [...rounds].sort((left, right) => {
        const leftId = Number(left?.id || left?.round_id || 0);
        const rightId = Number(right?.id || right?.round_id || 0);
        return rightId - leftId;
      });
    },

    getRoundAthleteBaseOrder(round = null) {
      const userId = Number(round?.user_id ?? round?.user?.id ?? 0);
      if (!userId) return null;

      const orderNumber = Number(this.athleteCompetitionOrderMap?.[userId]);
      return Number.isFinite(orderNumber) ? orderNumber : null;
    },

    getRoundScoreValue(round = null) {
      const score = Number.parseFloat(round?.total_score);
      return Number.isFinite(score) ? score : null;
    },

    compareRoundsByFallback(left, right) {
      const leftBaseOrder = this.getRoundAthleteBaseOrder(left);
      const rightBaseOrder = this.getRoundAthleteBaseOrder(right);

      if (leftBaseOrder !== null && rightBaseOrder !== null && leftBaseOrder !== rightBaseOrder) {
        return leftBaseOrder - rightBaseOrder;
      }

      if (leftBaseOrder !== null && rightBaseOrder === null) return -1;
      if (leftBaseOrder === null && rightBaseOrder !== null) return 1;

      const leftId = Number(left?.id || left?.round_id || 0);
      const rightId = Number(right?.id || right?.round_id || 0);
      return leftId - rightId;
    },

    sortRoundsForDisplay(rounds = [], { phase = 'qualification1' } = {}) {
      const list = Array.isArray(rounds) ? [...rounds] : [];

      return list.sort((left, right) => {
        if (phase === 'qualification1') {
          return this.compareRoundsByFallback(left, right);
        }

        const leftScore = this.getRoundScoreValue(left);
        const rightScore = this.getRoundScoreValue(right);

        if (leftScore !== null && rightScore !== null && leftScore !== rightScore) {
          return phase === 'qualification2'
            ? leftScore - rightScore
            : rightScore - leftScore;
        }

        if (leftScore !== null && rightScore === null) return -1;
        if (leftScore === null && rightScore !== null) return 1;

        return this.compareRoundsByFallback(left, right);
      });
    },

    getCurrentSelectionSortContext() {
      if (this.selectedRoundNumber === 2) {
        return { phase: 'qualification2' };
      }

      if (this.selectedStageName !== null && this.selectedStageName !== undefined) {
        return { phase: 'elimination', stageId: this.selectedStageName };
      }

      return { phase: 'qualification1' };
    },

    shouldUseVsOperationOrdering(rounds = []) {
      if (!Array.isArray(rounds) || !rounds.length) {
        return false;
      }

      if (!Number(this.competition?.is_vs || 0)) {
        return false;
      }

      return rounds.some((round) => {
        const bracket = round?.vs_bracket || null;
        const group = String(bracket?.group || round?.vs_group || '').trim();
        const matchNumber = Number(bracket?.match_number ?? round?.vs_match_number ?? 0);
        return group !== '' || matchNumber > 0;
      });
    },

    sortVsRoundsForOperations(rounds = []) {
      const normalizedRounds = (Array.isArray(rounds) ? rounds : []).map((round) => ({
        ...round,
        originalRound: round,
        round_id: round?.id ?? round?.round_id ?? null,
        id: round?.user?.id ?? round?.user_id ?? round?.id ?? null,
      }));

      const matches = assembleVsBracketMatches(normalizedRounds, {
        stageId: this.selectedStageName || this.selectedRoundNumber || 'judge-stage',
        placeholderName: this.$t('rounds.placeholder_player') || 'TBD',
      });

      const ordered = matches
        .flatMap((match) => match.participants || [])
        .map((participant) => participant?.sourceRound?.originalRound || participant?.sourceRound || null)
        .filter((round) => round && round.id);

      if (!ordered.length) {
        return this.sortRoundsForDisplay(rounds, this.getCurrentSelectionSortContext());
      }

      const seen = new Set(ordered.map((round) => String(round.id)));
      const remaining = (Array.isArray(rounds) ? rounds : []).filter((round) => !seen.has(String(round?.id || '')));

      return [...ordered, ...remaining];
    },

    getRoundsForCurrentSelection() {
      if (this.selectedRoundNumber === 1) {
        return Array.isArray(this.roundsBySection?.qualification1) ? this.roundsBySection.qualification1 : [];
      }

      if (this.selectedRoundNumber === 2) {
        return Array.isArray(this.roundsBySection?.qualification2) ? this.roundsBySection.qualification2 : [];
      }

      if (this.selectedStageName !== null && this.selectedStageName !== undefined) {
        const match = (this.roundsBySection?.eliminations || []).find(
          (entry) => String(entry.stageId) === String(this.selectedStageName),
        );
        return Array.isArray(match?.rounds) ? match.rounds : [];
      }

      return [];
    },

    findPreferredLiveRoundId() {
      const directCandidates = [
        this.activeRunningRoundId,
        this.resolveRoundIdForAthlete(this.pendingRecordingAthlete?.id) || this.pendingRecordingAthlete?.round_id,
        this.activeTimerControls[0]?.roundId,
      ].filter(Boolean);

      if (directCandidates.length) {
        return Number(directCandidates[0]) || null;
      }

      const allRounds = this.sortRoundsNewestFirst(Array.isArray(this.competitionRounds) ? this.competitionRounds : []);
      const activeRound = allRounds.find((round) => this.isRoundFocusableForLive(round));
      if (activeRound) {
        return Number(activeRound.id || activeRound.round_id) || null;
      }

      const selectedRounds = this.sortRoundsNewestFirst(this.getRoundsForCurrentSelection());
      const selectedActiveRound = selectedRounds.find((round) => this.isRoundFocusableForLive(round));
      if (selectedActiveRound) {
        return Number(selectedActiveRound.id || selectedActiveRound.round_id) || null;
      }
      if (selectedRounds.length) {
        return Number(selectedRounds[0].id || selectedRounds[0].round_id) || null;
      }

      return allRounds.length ? Number(allRounds[0].id || allRounds[0].round_id) || null : null;
    },

    async toggleLiveService(nextActive) {
      const competitionId = this.managedCompetitionId;
      if (!competitionId || this.liveServiceBusy) return;

      if (!this.liveChannelState) {
        await this.loadLiveChannelState();
      }

      const normalizedChannel = this.normalizeLiveChannelState(this.liveChannelState || {});
      const preferredRoundId = nextActive ? this.findPreferredLiveRoundId() : null;
      const nextChannel = {
        ...normalizedChannel,
        services: normalizedChannel.services.map((service) => {
          if (service.service_type !== 'round_viewer') {
            return nextActive
              ? {
                ...service,
                enabled: false,
                status: 'inactive',
              }
              : service;
          }

          return {
            ...service,
            enabled: Boolean(nextActive),
            status: nextActive ? 'active' : 'inactive',
            round_id: nextActive ? (preferredRoundId || service.round_id || null) : (service.round_id || null),
            config: {
              ...(service.config && typeof service.config === 'object' ? service.config : {}),
              round_id: nextActive ? (preferredRoundId || service.config?.round_id || service.round_id || null) : (service.config?.round_id || service.round_id || null),
            },
          };
        }),
      };

      this.liveServiceBusy = true;

      try {
        const response = await this.$axios.put(
          `/competitions/${competitionId}/live-channel`,
          this.buildLiveChannelPayload(nextChannel),
        );
        this.applyLiveChannelState(response.data?.channel || nextChannel);
      } catch (error) {
        console.warn('[Live] failed to toggle round viewer service', error?.response?.data?.message || error?.message || error);
      } finally {
        this.liveServiceBusy = false;
      }
    },

    syncTimerThemeFromCompetition() {
      const theme = this.competition?.timer_theme
        || this.competition?.timerTheme
        || this.competitionData?.timer_theme
        || this.competitionData?.timerTheme
        || 'dark';
      this.timerTheme = normalizeTimerTheme(theme);
    },

    isAllowedTimerTheme(theme) {
      return isAllowedTimerTheme(theme);
    },

    normalizeTimerTheme(theme) {
      return normalizeTimerTheme(theme);
    },

    normalizeTimerTimestampMs(value) {
      if (value === null || value === undefined || value === '') return NaN;

      const numeric = Number(value);
      if (Number.isFinite(numeric) && numeric > 0) {
        return numeric < 1e11 ? numeric * 1000 : numeric;
      }

      const parsed = Date.parse(value);
      return Number.isFinite(parsed) ? parsed : NaN;
    },

    normalizeTimerVersionMs(value, fallbackUpdatedAt = null) {
      const numeric = Number(value);
      if (Number.isFinite(numeric) && numeric > 0) {
        return numeric < 1e12 ? numeric * 1000 : numeric;
      }

      const parsed = this.normalizeTimerTimestampMs(fallbackUpdatedAt);
      return Number.isFinite(parsed) ? parsed : 0;
    },

    normalizeTimerResponse(payload = {}) {
      const raw = payload?.timer && typeof payload.timer === 'object' ? payload.timer : payload;
      const timer = { ...raw };
      const nowMs = Date.now();

      const numericFields = [
        'id',
        'round_id',
        'duration_seconds',
        'default_duration_seconds',
        'remaining_seconds',
        'duration',
        'version',
        'version_ms',
        'server_now_ms',
        'duration_ms',
        'remaining_ms_snapshot',
        'last_started_at_ms',
        'snapshot_emitted_at_ms',
        'client_received_at_ms',
      ];
      numericFields.forEach((field) => {
        const value = Number(timer[field]);
        if (timer[field] !== undefined && Number.isFinite(value)) {
          timer[field] = value;
        }
      });

      const versionMs = this.normalizeTimerVersionMs(timer.version_ms ?? timer.version, timer.updated_at);
      if (versionMs > 0) {
        timer.version_ms = versionMs;
        timer.version = versionMs;
      }

      const serverNowMs = this.normalizeTimerTimestampMs(timer.server_now_ms ?? timer.server_now);
      if (Number.isFinite(serverNowMs)) {
        timer.server_now_ms = serverNowMs;
        this.timerServerDriftMs = nowMs - serverNowMs;
      }

      const durationMs = Number.isFinite(timer.duration_ms)
        ? timer.duration_ms
        : (Number.isFinite(timer.duration_seconds)
          ? timer.duration_seconds * 1000
          : (Number.isFinite(timer.duration) ? timer.duration * 1000 : NaN));
      if (Number.isFinite(durationMs)) {
        timer.duration_ms = durationMs;
        timer.duration_seconds = durationMs / 1000;
        timer.duration = durationMs / 1000;
      }

      const remainingMsSnapshot = Number.isFinite(timer.remaining_ms_snapshot)
        ? timer.remaining_ms_snapshot
        : (Number.isFinite(timer.remaining_seconds)
          ? timer.remaining_seconds * 1000
          : (Number.isFinite(timer.duration_ms) ? timer.duration_ms : NaN));
      if (Number.isFinite(remainingMsSnapshot)) {
        timer.remaining_ms_snapshot = remainingMsSnapshot;
        if (!Number.isFinite(timer.remaining_seconds)) {
          timer.remaining_seconds = remainingMsSnapshot / 1000;
        }
      }

      const normalizedState = String(timer.state || '').toLowerCase();
      if (normalizedState === 'paused' && Number.isFinite(timer.remaining_seconds)) {
        timer.remaining_ms_snapshot = timer.remaining_seconds * 1000;
      }

      const lastStartedAtMs = this.normalizeTimerTimestampMs(timer.last_started_at_ms ?? timer.last_started_at);
      if (Number.isFinite(lastStartedAtMs)) {
        timer.last_started_at_ms = lastStartedAtMs;
      }

      const snapshotEmittedAtMs = this.normalizeTimerTimestampMs(timer.snapshot_emitted_at_ms);
      if (Number.isFinite(snapshotEmittedAtMs)) {
        timer.snapshot_emitted_at_ms = snapshotEmittedAtMs;
      } else if (Number.isFinite(timer.server_now_ms)) {
        timer.snapshot_emitted_at_ms = timer.server_now_ms;
      }

      timer.client_received_at_ms = Number(timer.client_received_at_ms) || nowMs;

      if (timer.remaining_seconds === undefined && Number.isFinite(timer.duration_seconds)) {
        timer.remaining_seconds = timer.duration_seconds;
      } else if (timer.remaining_seconds === undefined && Number.isFinite(timer.duration)) {
        timer.remaining_seconds = timer.duration;
      }

      if (!timer.state) timer.state = 'idle';
      return timer;
    },

    isIncomingTimerSnapshotStale(existing = null, incoming = null) {
      if (!existing || !incoming) return false;

      const existingVersion = Number(existing?.version_ms ?? existing?.version);
      const incomingVersion = Number(incoming?.version_ms ?? incoming?.version);
      if (Number.isFinite(existingVersion) && Number.isFinite(incomingVersion)) {
        return incomingVersion < existingVersion;
      }

      const existingUpdatedAt = this.normalizeTimerTimestampMs(existing?.updated_at);
      const incomingUpdatedAt = this.normalizeTimerTimestampMs(incoming?.updated_at);
      if (Number.isFinite(existingUpdatedAt) && Number.isFinite(incomingUpdatedAt)) {
        return incomingUpdatedAt < existingUpdatedAt;
      }

      return false;
    },

    applyAuthoritativeTimerState(roundId, payload = {}) {
      if (!roundId) return false;

      const normalized = this.normalizeTimerResponse({ ...payload, round_id: payload?.round_id ?? payload?.roundId ?? roundId });
      const existing = this.timerStateByRound[roundId] || null;
      if (this.isIncomingTimerSnapshotStale(existing, normalized)) {
        return false;
      }

      this.updateTimerState(roundId, normalized);
      if (Number.isFinite(normalized.duration_seconds)) {
        this.timerDurationInputs = { ...this.timerDurationInputs, [roundId]: normalized.duration_seconds };
      }
      return true;
    },

    applyRealtimeRoundTimers(round = {}, envelope = {}) {
      if (!round || typeof round !== 'object') return;

      const trackedRoundIds = new Set(this.trackedTimerRoundIds);

      const roundId = round?.id || round?.round_id;
      const directTimer = round?.timer && typeof round.timer === 'object'
        ? { ...round.timer, round_id: round.timer?.round_id ?? round.timer?.roundId ?? roundId }
        : null;
      const timers = Array.isArray(round?.timers)
        ? round.timers.map((timer) => ({
          ...timer,
          round_id: timer?.round_id ?? timer?.roundId ?? roundId,
          competition_id: timer?.competition_id ?? envelope?.competition_id ?? this.competition?.id ?? this.competitionIdToView,
        }))
        : [];

      timers.forEach((timer) => {
        const timerRoundId = timer?.round_id || timer?.roundId;
        if (!timerRoundId) return;
        if (trackedRoundIds.size && !trackedRoundIds.has(String(timerRoundId))) return;
        this.applyAuthoritativeTimerState(timerRoundId, timer);
      });

      if (directTimer) {
        const directRoundId = directTimer?.round_id || directTimer?.roundId;
        if (directRoundId) {
          if (trackedRoundIds.size && !trackedRoundIds.has(String(directRoundId))) return;
          this.applyAuthoritativeTimerState(directRoundId, directTimer);
        }
      }
    },

    matchesTimerPayload(payload = {}) {
      const source = this.unwrapRealtimePayload(payload) || {};
      const payloadCode = String(source?.access_code || source?.accessCode || source?.competition_code || source?.competitionCode || '').trim();
      const currentCode = String(this.timerAccessCode || '').trim();
      if (payloadCode && currentCode) {
        return payloadCode === currentCode;
      }

      const payloadCompetitionId = Number(source?.competition_id || source?.competitionId || source?.round?.competition_id || source?.round?.competitionId);
      const currentCompetitionId = Number(this.competition?.id || this.competitionIdToView);
      return Number.isFinite(payloadCompetitionId) && Number.isFinite(currentCompetitionId)
        ? payloadCompetitionId === currentCompetitionId
        : true;
    },

    applyTimerRealtimePayload(raw = {}) {
      const source = this.unwrapRealtimePayload(raw);
      if (!source || !this.matchesTimerPayload(source)) return;
      if (!this.shouldTrackLiveTimers) return;

      const trackedRoundIds = new Set(this.trackedTimerRoundIds);

      if (this.isAllowedTimerTheme(source?.theme)) {
        this.timerTheme = this.normalizeTimerTheme(source.theme);
      }

      if (source?.round && typeof source.round === 'object') {
        this.applyRealtimeRoundTimers(source.round, source);
      }

      const timerPayload = source?.timer && typeof source.timer === 'object' ? source.timer : null;
      if (timerPayload) {
        const roundId = timerPayload?.round_id || timerPayload?.roundId || source?.round_id || source?.roundId;
        if (roundId) {
          if (trackedRoundIds.size && !trackedRoundIds.has(String(roundId))) return;
          this.applyAuthoritativeTimerState(roundId, {
            ...timerPayload,
            round_id: roundId,
            competition_id: timerPayload?.competition_id ?? source?.competition_id ?? this.competition?.id ?? this.competitionIdToView,
          });
        }
      }

      this.refreshTimerTicker();
    },

    openPublicTimer() {
      const code = this.timerAccessCode;
      if (!code) return;
      const base = window?.location?.origin || '';
      const url = `${base}/#/timer-code/${encodeURIComponent(code)}`;
      window.open(url, '_blank', 'noopener,noreferrer');
    },

    timerDisplayForAthlete(athlete) {
      const roundId = this.resolveRoundIdForAthlete(athlete?.id) || athlete?.round_id || athlete?.id;
      if (!roundId) return '00:00';
      const state = this.timerStateByRound[roundId];
      return this.formatTimerValue(this.getEffectiveTimerDisplaySeconds(state));
    },

    getEffectiveTimerDisplaySeconds(state = {}) {
      const timerPayload = state?.timer && typeof state.timer === 'object' ? state.timer : state;
      const timerState = String(timerPayload?.state || 'idle').toLowerCase();
      const remainingMsSnapshot = Number(timerPayload?.remaining_ms_snapshot);
      const durationMs = Number(timerPayload?.duration_ms);
      const remaining = Number(timerPayload?.remaining_seconds);
      const duration = Number.isFinite(durationMs)
        ? durationMs / 1000
        : Number(timerPayload?.duration_seconds ?? timerPayload?.duration);
      const hasRemaining = Number.isFinite(remaining);
      const hasDuration = Number.isFinite(duration);

      // Paused timers must reflect the persisted remaining_seconds from the backend.
      if (timerState === 'paused' && hasRemaining) {
        return remaining;
      }

      if (timerState === 'running' && Number.isFinite(remainingMsSnapshot)) {
        const serverNowMs = Number(timerPayload?.server_now_ms);
        const currentServerNowMs = this.resolveCurrentTimerServerNowMs(timerPayload);
        const elapsedMs = Number.isFinite(serverNowMs)
          ? Math.max(0, currentServerNowMs - serverNowMs)
          : Math.max(0, this.timerTickNowMs - Number(timerPayload?.client_received_at_ms || this.timerTickNowMs));
        return (remainingMsSnapshot - elapsedMs) / 1000;
      }

      if (timerState === 'running' || timerState === 'paused' || timerState === 'finished') {
        if (Number.isFinite(remainingMsSnapshot)) return remainingMsSnapshot / 1000;
        if (hasRemaining) return remaining;
        if (hasDuration) return duration;
        return 0;
      }

      if (hasDuration) return duration;
      if (hasRemaining) return remaining;
      return 0;
    },

    resolveCurrentTimerServerNowMs(state = {}) {
      if (Number.isFinite(this.timerServerDriftMs)) {
        return this.timerTickNowMs - this.timerServerDriftMs;
      }

      const serverNowMs = Number(state?.server_now_ms);
      const clientReceivedAtMs = Number(state?.client_received_at_ms);
      if (Number.isFinite(serverNowMs) && Number.isFinite(clientReceivedAtMs)) {
        return serverNowMs + Math.max(0, this.timerTickNowMs - clientReceivedAtMs);
      }

      return this.timerTickNowMs;
    },

    async setActiveTimerRound(roundId) {
      const competitionId = this.competition?.id || this.competitionIdToView || this.competitionIdFromRound(roundId);
      if (!competitionId) return;

      try {
        await this.$axios.post('/timers/active', {
          competition_id: competitionId,
          round_id: roundId || null,
        }, { headers: this.timerAuthHeaders() });
      } catch (err) {
        console.warn('[Timer] active timer update failed', err?.response?.data || err?.message);
      }
    },

    async clearActiveTimer() {
      await this.setActiveTimerRound(null);
      this.clearLegacyTimerStorage();
      this.timerMenuOpen = false;
    },

    clearLegacyTimerStorage() {
      const legacyKeys = ['timerForceSync', 'timerTheme'];

      legacyKeys.forEach((key) => {
        try {
          window?.localStorage?.removeItem(key);
        } catch (e) {
          /* ignore */
        }

        try {
          window?.sessionStorage?.removeItem(key);
        } catch (e) {
          /* ignore */
        }
      });
    },

    updateTimerState(roundId, payload = {}) {
      if (!roundId) return;
      const mergedState = this.normalizeTimerResponse({ ...(this.timerStateByRound[roundId] || {}), ...payload });
      this.timerStateByRound = {
        ...this.timerStateByRound,
        [roundId]: mergedState,
      };
    },

    async ensureTimerForRound(roundId) {
      if (!roundId || this.timerStateByRound[roundId]?.id) return;
      try {
        const resp = await this.$axios.post('/timers', { round_id: roundId }, { headers: this.timerAuthHeaders() });
        const timer = this.normalizeTimerResponse(resp?.data || {});
        this.applyAuthoritativeTimerState(roundId, timer);
      } catch (err) {
        try {
          await this.fetchTimer(roundId);
        } catch (e) {
          console.warn('[Timer] no se pudo inicializar', e?.response?.data || e?.message);
        }
      }
    },

    async fetchTimer(roundId) {
      if (!roundId) return null;
      try {
        const resp = await this.$axios.get(`/timers/${roundId}`, { headers: this.timerAuthHeaders() });
        const timer = this.normalizeTimerResponse(resp?.data || {});
        this.applyAuthoritativeTimerState(roundId, timer);
        return timer;
      } catch (err) {
        console.warn('[Timer] fetchTimer failed', { roundId, error: err?.response?.data || err?.message });
        return null;
      }
    },

    async ensureTimersForTargets(targets = []) {
      const list = Array.isArray(targets) ? targets.filter(Boolean) : [];
      for (const ath of list) {
        const roundId = this.resolveRoundIdForAthlete(ath?.id) || ath?.round_id || ath?.id;
        if (this.applyRoundTimerState(roundId)) continue;
        await this.ensureTimerForRound(roundId);
      }
    },

    applyLocalTimerTransition(roundId, action, payload = {}) {
      if (!roundId) return;
      const prev = this.timerStateByRound[roundId] || {};
      const baseDuration = Number(payload.duration_seconds ?? payload.duration ?? prev.duration_seconds ?? prev.duration);
      const duration = Number.isFinite(baseDuration) ? baseDuration : 0;
      const liveRemaining = this.getEffectiveTimerDisplaySeconds(prev);
      const remaining = Number.isFinite(liveRemaining)
        ? liveRemaining
        : (Number.isFinite(prev.remaining_seconds) ? prev.remaining_seconds : duration);
      const nowMs = Date.now();
      const next = { ...prev };

      switch (action) {
        case 'set-duration':
          next.duration_seconds = duration || prev.duration_seconds;
          next.remaining_seconds = duration || prev.remaining_seconds;
          next.duration_ms = (duration || prev.duration_seconds || 0) * 1000;
          next.remaining_ms_snapshot = (duration || prev.remaining_seconds || 0) * 1000;
          next.server_now_ms = nowMs;
          next.snapshot_emitted_at_ms = nowMs;
          next.client_received_at_ms = nowMs;
          if (!next.state) next.state = 'idle';
          break;
        case 'play':
          next.state = 'running';
          next.remaining_seconds = remaining || duration;
          next.remaining_ms_snapshot = (remaining || duration || 0) * 1000;
          next.server_now_ms = nowMs;
          next.snapshot_emitted_at_ms = nowMs;
          next.client_received_at_ms = nowMs;
          break;
        case 'pause':
          next.state = 'paused';
          next.remaining_seconds = remaining;
          next.remaining_ms_snapshot = remaining * 1000;
          next.server_now_ms = nowMs;
          next.snapshot_emitted_at_ms = nowMs;
          next.client_received_at_ms = nowMs;
          break;
        case 'reset':
          next.state = 'idle';
          next.remaining_seconds = duration || prev.remaining_seconds;
          next.remaining_ms_snapshot = (duration || prev.remaining_seconds || 0) * 1000;
          next.server_now_ms = nowMs;
          next.snapshot_emitted_at_ms = nowMs;
          next.client_received_at_ms = nowMs;
          break;
        case 'finish':
          next.state = 'finished';
          if (payload.remaining_seconds !== undefined) {
            const rs = Number(payload.remaining_seconds);
            if (Number.isFinite(rs)) next.remaining_seconds = rs;
          }
          next.remaining_ms_snapshot = Number.isFinite(next.remaining_seconds)
            ? next.remaining_seconds * 1000
            : (remaining * 1000);
          next.server_now_ms = nowMs;
          next.snapshot_emitted_at_ms = nowMs;
          next.client_received_at_ms = nowMs;
          break;
        default:
          break;
      }

      this.updateTimerState(roundId, next);
    },

    shouldApplyOptimisticTimerTransition(roundId, action) {
      if (!roundId) return false;
      if (!this.shouldTrackLiveTimers) return true;

      const isTrackedRound = this.trackedTimerRoundIds.includes(String(roundId));
      if (!isTrackedRound) return true;

      return ['set-duration', 'play', 'pause', 'reset', 'finish'].includes(action);
    },

    hasUsableTimerSnapshot(timer = null, roundId = null) {
      if (!timer || typeof timer !== 'object') return false;

      const snapshotRoundId = timer.round_id ?? timer.roundId ?? timer.id;
      if (roundId && snapshotRoundId && String(snapshotRoundId) !== String(roundId)) {
        return false;
      }

      return [
        timer.state,
        timer.version_ms,
        timer.version,
        timer.remaining_ms_snapshot,
        timer.remaining_seconds,
        timer.duration_ms,
        timer.duration_seconds,
      ].some((value) => value !== undefined && value !== null);
    },

    async timerAction(roundId, action, payload = {}) {
      if (!roundId) return;
      if (this.shouldApplyOptimisticTimerTransition(roundId, action)) {
        this.applyLocalTimerTransition(roundId, action, payload);
      }

      let serverTimer = null;
      try {
        const resp = await this.$axios.post(`/timers/${roundId}/${action}`, payload, { headers: this.timerAuthHeaders() });
        const normalized = this.normalizeTimerResponse(resp?.data || {});
        if (this.hasUsableTimerSnapshot(normalized, roundId)) {
          this.applyAuthoritativeTimerState(roundId, normalized);
          serverTimer = normalized;
        }
      } catch (err) {
        console.warn('[Timer]', action, err?.response?.data || err?.message);
      }

      if (!serverTimer) {
        serverTimer = await this.fetchTimer(roundId);
      }
    },

    playTimer(roundId) {
      return this.timerAction(roundId, 'play');
    },

    pauseTimer(roundId) {
      return this.timerAction(roundId, 'pause');
    },

    toggleTimerControl(ctrl) {
      if (!ctrl || !ctrl.roundId) return;
      if (ctrl.disableAll) return;
      if (ctrl.isRunning) {
        this.pauseTimer(ctrl.roundId);
      } else {
        this.playTimer(ctrl.roundId);
      }
    },

    resetTimer(roundId) {
      return this.timerAction(roundId, 'reset');
    },

    finishTimer(roundId, remainingSeconds = undefined) {
      const payload = Number.isFinite(Number(remainingSeconds))
        ? { remaining_seconds: Number(remainingSeconds) }
        : {};
      return this.timerAction(roundId, 'finish', payload);
    },

    async applyTimerDuration(roundId) {
      if (!roundId) return;
      const seconds = Number(this.timerDurationInputs[roundId]);
      if (!Number.isFinite(seconds) || seconds <= 0) return;
      await this.timerAction(roundId, 'set-duration', { duration_seconds: seconds });
    },

    toggleTimerMenu() {
      this.timerMenuOpen = !this.timerMenuOpen;
      if (this.timerMenuOpen && this.activeTimerControls.length) {
        this.seedTimerMenuDuration();
      }
    },

    async setTimerTheme(theme) {
      const next = this.normalizeTimerTheme(theme);
      if (next === this.timerTheme) return;

      try {
        const competitionId = this.competition?.id || this.competitionIdToView;
        if (!competitionId) return;
        await this.$axios.post('/timers/theme', {
          competition_id: competitionId,
          theme: next,
        }, { headers: this.timerAuthHeaders() });
        this.timerTheme = next;
      } catch (e) {
        console.warn('[Timer] theme update failed', e?.response?.data || e?.message);
      }
    },

    seedTimerMenuDuration() {
      const first = this.activeTimerControls[0];
      const raw = first?.editValue ?? first?.display;
      let num = Number(raw);

      if (!Number.isFinite(num) && typeof raw === 'string' && raw.includes(':')) {
        const [m, s] = raw.split(':').map((part) => Number(part));
        if (Number.isFinite(m) && Number.isFinite(s)) {
          num = (m * 60) + s;
        }
      }

      this.timerMenuDuration = Number.isFinite(num) && num > 0 ? num : 60;
    },

    applySharedTimerDuration() {
      if (this.hasRunningTimerInMenu) return;
      const seconds = Number(this.timerMenuDuration);
      if (!Number.isFinite(seconds) || seconds <= 0) return;

      const roundIds = (this.isVsTimerContext
        ? this.activeTimerControls.map((c) => c.roundId)
        : [this.activeTimerControls[0]?.roundId]
      ).filter(Boolean);

      roundIds.forEach((rid) => {
        this.timerDurationInputs = { ...this.timerDurationInputs, [rid]: seconds };
        this.timerAction(rid, 'set-duration', { duration_seconds: seconds });
      });
    },

    resetFromMenu(roundId) {
      if (this.hasRunningTimerInMenu) return;
      if (!roundId) return;
      this.resetTimer(roundId);
    },

    onTimerInput(roundId, value) {
      const seconds = Number(value);
      this.timerDurationInputs = { ...this.timerDurationInputs, [roundId]: seconds };
    },

    startTimerTicker() {
      if (this.timerTickHandle) return;
      this.timerTickNowMs = Date.now();
      this.timerTickHandle = setInterval(() => this.tickTimers(), 250);
    },

    stopTimerTicker() {
      if (!this.timerTickHandle) return;
      clearInterval(this.timerTickHandle);
      this.timerTickHandle = null;
    },

    refreshTimerTicker() {
      this.timerTickNowMs = Date.now();
      if (this.shouldTrackLiveTimers && this.hasRunningTrackedTimer) {
        this.startTimerTicker();
        return;
      }
      this.stopTimerTicker();
    },

    tickTimers() {
      this.timerTickNowMs = Date.now();
    },

    subscribeToTimerUpdates() {
      const accessCode = this.timerAccessCode;
      const channelName = accessCode ? `timer-code.${accessCode}` : '';
      if (!channelName || !this.shouldTrackLiveTimers) {
        this.unsubscribeFromTimerUpdates();
        return;
      }
      if (this.timerChannelName === channelName && this.timerChannel) return;

      this.unsubscribeFromTimerUpdates();

      try {
        const channel = echoService.channel(channelName);
        channel.listen('.CompetitionTimerUpdated', this.applyTimerRealtimePayload);
        channel.listen('.CompetitionRoundContextUpdated', this.applyTimerRealtimePayload);
        channel.listen('.CompetitionActiveTimerChanged', this.applyTimerRealtimePayload);
        channel.listen('.CompetitionTimerThemeChanged', this.applyTimerRealtimePayload);
        this.timerChannel = channel;
        this.timerChannelName = channelName;
      } catch (err) {
        console.warn('[Timer] failed to subscribe to timer updates', err?.message || err);
      }
    },

    unsubscribeFromTimerUpdates() {
      if (this.timerChannel) {
        try {
          this.timerChannel.stopListening('.CompetitionTimerUpdated');
          this.timerChannel.stopListening('.CompetitionRoundContextUpdated');
          this.timerChannel.stopListening('.CompetitionActiveTimerChanged');
          this.timerChannel.stopListening('.CompetitionTimerThemeChanged');
        } catch (e) {
          /* ignore */
        }
      }

      if (this.timerChannelName) {
        try {
          echoService.leave(this.timerChannelName);
        } catch (e) {
          /* ignore */
        }
      }

      this.timerChannel = null;
      this.timerChannelName = '';
    },

    subscribeToPlaylistUpdates() {
      const competitionId = this.competitionIdToView || this.competition?.id;
      if (!competitionId) return;
      const channelName = `competition.${competitionId}`;
      if (this.uploadChannelName === channelName && this.uploadChannel) return;

      this.unsubscribeFromPlaylistUpdates();

      try {
        const channel = echoService.channel(channelName);
        channel.listen('.PlaylistVideoUploadUpdated', this.queueUploadBroadcast);
        this.uploadChannel = channel;
        this.uploadChannelName = channelName;
      } catch (err) {
        console.warn('[Upload] failed to subscribe to playlist updates', err?.message || err);
      }
    },

    unsubscribeFromPlaylistUpdates() {
      if (this.uploadBroadcastFlushHandle) {
        clearTimeout(this.uploadBroadcastFlushHandle);
        this.uploadBroadcastFlushHandle = null;
      }
      this.queuedUploadBroadcasts = {};

      if (this.uploadChannel) {
        try {
          this.uploadChannel.stopListening('.PlaylistVideoUploadUpdated');
        } catch (e) {
          /* ignore */
        }
      }
      this.uploadChannel = null;
      this.uploadChannelName = '';
    },

    queueUploadBroadcast(event = {}) {
      const targetIds = this.extractRoundIdsFromBroadcast(event);
      if (!targetIds.length) {
        this.handleUploadBroadcast(event);
        return;
      }

      const queueKey = targetIds.map((id) => String(id)).sort().join(':');
      this.queuedUploadBroadcasts = {
        ...this.queuedUploadBroadcasts,
        [queueKey]: event,
      };

      if (this.uploadBroadcastFlushHandle) {
        clearTimeout(this.uploadBroadcastFlushHandle);
      }

      this.uploadBroadcastFlushHandle = setTimeout(() => {
        this.flushQueuedUploadBroadcasts();
      }, 140);
    },

    flushQueuedUploadBroadcasts() {
      if (this.uploadBroadcastFlushHandle) {
        clearTimeout(this.uploadBroadcastFlushHandle);
        this.uploadBroadcastFlushHandle = null;
      }

      const queued = Object.values(this.queuedUploadBroadcasts || {});
      this.queuedUploadBroadcasts = {};
      queued.forEach((event) => {
        this.handleUploadBroadcast(event);
      });
    },

    handleUploadBroadcast(event = {}) {
      const playlistId = event.playlist_id || event.playlistId || event.playlist;
      if (playlistId && this.cameraPlaylistId && String(playlistId) !== String(this.cameraPlaylistId)) {
        return;
      }

      const targetIds = this.extractRoundIdsFromBroadcast(event);
      if (!targetIds.length) return;

      const status = (event.status || '').toLowerCase();
      const percentFromChunks = Number.isFinite(event.received_chunks) && Number.isFinite(event.total_chunks)
        ? Math.round(((event.received_chunks || 0) / (event.total_chunks || 1)) * 100)
        : null;
      const percentRaw = event.percent ?? event.progress ?? percentFromChunks;
      const percent = Number.isFinite(percentRaw)
        ? Math.max(0, Math.min(100, Math.round(percentRaw)))
        : (this.uploadProgress?.[targetIds[0]]?.percent || 0);
      const processingMsg = this.$t?.('competitions.youtube.processing') || 'Procesando en YouTube...';
      const message = event.message || '';

      this.applyBroadcastPlaylistVideoState(targetIds, event);

      if (status === 'uploaded') {
        this.setUploadProgressForTargets(targetIds, { percent: 100, status: 'done', message: '' });
        if (event.youtube_url || event.youtubeUrl || event.video_link) {
          this.applyBroadcastYoutubeUrl(targetIds, event.youtube_url || event.youtubeUrl || event.video_link);
        }
        targetIds.forEach((id) => this.clearStoredUpload(this.cameraPlaylistId, id));
        return;
      }

      if (status === 'processing_youtube') {
        this.setUploadProgressForTargets(targetIds, {
          percent: 100,
          status: 'processing_youtube',
          message: processingMsg,
        });
        return;
      }

      if (status === 'failed') {
        const errorMsg = message || 'Fallo al procesar en YouTube';
        this.setUploadProgressForTargets(targetIds, {
          percent: 100,
          status: 'error',
          message: errorMsg,
        });
        this.manualUploadError = errorMsg;
        return;
      }

      // Default to uploading updates (progress or keep-alive)
      this.setUploadProgressForTargets(targetIds, {
        percent,
        status: 'uploading',
        message: message || '',
      });
    },

    extractRoundIdsFromBroadcast(event = {}) {
      const ids = [];
      ['round_ids', 'rounds', 'target_ids'].forEach((key) => {
        const val = event[key];
        if (Array.isArray(val)) ids.push(...val);
      });
      ['round_id', 'roundId'].forEach((key) => {
        if (event[key]) ids.push(event[key]);
      });
      return [...new Set(ids.filter(Boolean))];
    },

    applyBroadcastPlaylistVideoState(roundIds, event = {}) {
      if (!Array.isArray(roundIds) || !roundIds.length) return;

      const playlistVideoId = event.video_id || event.videoId || event.playlist_video_id || null;
      const status = (event.status || '').toLowerCase();
      const youtubeStatus = status === 'uploaded'
        ? 'uploaded'
        : (status === 'failed' ? 'failed' : (status === 'processing_youtube' ? 'pending' : ''));

      roundIds.forEach((roundId) => {
        const match = (this.competitionRounds || []).find((round) => String(round?.id || round?.round_id) === String(roundId));
        if (!match) return;

        if (playlistVideoId) {
          if (this.$set) {
            this.$set(match, 'playlist_video_id', playlistVideoId);
          } else {
            match.playlist_video_id = playlistVideoId;
          }
        }

        const nextPlaylistVideo = {
          ...(match.playlistVideo || {}),
          ...(playlistVideoId ? { id: playlistVideoId } : {}),
          ...(status ? { status } : {}),
          ...(youtubeStatus ? { youtube_status: youtubeStatus } : {}),
        };

        if (Object.keys(nextPlaylistVideo).length) {
          if (this.$set) {
            this.$set(match, 'playlistVideo', nextPlaylistVideo);
          } else {
            match.playlistVideo = nextPlaylistVideo;
          }
        }
      });
    },

    applyBroadcastYoutubeUrl(roundIds, youtubeUrl) {
      if (!youtubeUrl || !Array.isArray(roundIds) || !roundIds.length) return;
      const rounds = this.competitionRounds || [];

      roundIds.forEach((roundId) => {
        const match = rounds.find((r) => String(r?.id || r?.round_id) === String(roundId));
        if (!match) return;
        if (this.$set) {
          this.$set(match, 'video_link', youtubeUrl);
        } else {
          match.video_link = youtubeUrl;
        }

        if (match.playlistVideo) {
          if (this.$set) {
            this.$set(match.playlistVideo, 'url', youtubeUrl);
          } else {
            match.playlistVideo.url = youtubeUrl;
          }
        } else if (this.$set) {
          this.$set(match, 'playlistVideo', { url: youtubeUrl });
        } else {
          match.playlistVideo = { url: youtubeUrl };
        }
      });
    },

    openYoutubeConfirm() {
      if (this.youtubePreparing || this.hasPlaylist) return;
      this.showYoutubeConfirm = true;
    },

    closeYoutubeConfirm() {
      this.showYoutubeConfirm = false;
    },

    confirmYoutubePrepare() {
      this.showYoutubeConfirm = false;
      this.handlePrepareYouTube();
    },

    selectRound(roundNumber) {
      this.restoredTablePage = 1;
      this.restoredTablePairPage = 1;
      this.selectedRoundNumber = roundNumber;
      this.selectedStageName = null;
    },

    selectStage(stageName) {
      this.restoredTablePage = 1;
      this.restoredTablePairPage = 1;
      this.selectedStageName = this.normalizeStageName(stageName);
      this.selectedRoundNumber = null;
    },

    ensureDefaultSelection() {
      // Si ya hay selección (o se restauró desde navegación), no tocar
      if (this.selectedRoundNumber !== null || this.selectedStageName !== null) return;

      const eliminations = this.roundsBySection?.eliminations || [];
      const hasElims = eliminations.length > 0;
      if (hasElims) {
        // La final suele tener el id más bajo; elegimos la última entrada (id menor)
        const finalStage = eliminations[eliminations.length - 1];
        this.selectStage(finalStage.stageId);
        return;
      }

      if (Array.isArray(this.roundsBySection?.qualification2) && this.roundsBySection.qualification2.length) {
        this.selectRound(2);
        return;
      }

      if (Array.isArray(this.roundsBySection?.qualification1) && this.roundsBySection.qualification1.length) {
        this.selectRound(1);
      }
    },

    getStageName(stageId) {
      if (!this.stages) return [];
      if (!stageId) return "";
      const stage = this.stages.find((s) => s.id === Number(stageId));
      const key = `rounds.stage${stage?.id}`;
      const transName = stage && this.$te(key) ? this.$t(key) : stage?.name;

      return stage ? transName : "";
    },
    normalizeStageName(stageId) {
      return stageId === null || stageId === undefined ? null : String(stageId);
    },

    startRecording(payload) {
      const pair = this.normalizeVsPairPayload(payload?.pair);
      const isPairPayload = pair.length > 1;
      const athlete = isPairPayload ? pair[0] : payload;

      if (this.isAnyTimerRunning) return;
      if (!athlete || !athlete.id) return;
      if (isPairPayload && pair.some(p => !p || !p.id)) return;
      // Eye click should only stage overlay and open camera; actual recording starts from CameraModal record button
      if (!this.cameraReady) return;
      this.ensureTimersForTargets(isPairPayload ? pair : [athlete]);
      this.pendingRecordingAthlete = athlete;
      this.pendingRecordingPair = pair;
      this.cameraRecordingStarted = false;
      this.recordingInProgressId = null;
      this.cameraOverlay = isPairPayload ? this.buildVsOverlay(pair) : this.buildCameraOverlay(athlete);
      this.cameraRecordingFileName = isPairPayload ? this.buildVsRecordingFileName(pair) : this.buildRecordingFileName(athlete);
      this.showCameraModal = true;

      const roundId = this.resolveRoundIdForAthlete(athlete?.id) || athlete?.round_id || athlete?.id;
      if (roundId) {
        this.setActiveTimerRound(roundId);
      }
    },

    stopRecording(payload) {
      const pair = this.normalizeVsPairPayload(payload?.pair);
      const isPairPayload = pair.length > 1;
      const athlete = isPairPayload ? pair[0] : payload;
      const emitPayload = { playlistId: this.cameraPlaylistId };

      if (this.isAnyTimerRunning) return;

      if (isPairPayload) {
        pair.forEach((item) => this.$emit("stop-recording-athlete", item, null, emitPayload));
      } else {
        this.$emit("stop-recording-athlete", athlete, null, emitPayload);
      }

      if (this.cameraWorkflowEnabled) {
        this.cleanupCameraModal();
      } else {
        this.setActiveTimerRound(null);
      }
    },

    startScoring({ athlete, stageName, roundNumber, tablePage, tablePairPage }) {
      this.$emit("score-athlete", { athlete, stageName, roundNumber, tablePage, tablePairPage });
    },

    editScoring({ athlete, stageName, roundNumber, tablePage, tablePairPage }) {
      this.$emit("edit-score", { athlete, stageName, roundNumber, tablePage, tablePairPage });
    },

    viewScore({ athlete, stageName, roundNumber, tablePage, tablePairPage }) {
      this.$emit("view-score", { athlete, stageName, roundNumber, tablePage, tablePairPage });
    },

    disqualifyAthlete(athlete) {
      this.controlPop("disqualify-athlete", athlete, this.$t('judge.modal.disqualify_athlete'));
    },

    markInjury(athlete) {
      this.controlPop("injury-athlete", athlete, this.$t('judge.modal.injury_athlete'));
    },

    getStatusLabel(status_id) {
      const labels = {
        13: this.$t("judge.pending"),
        6: this.$t("judge.scoring"),
        11: this.$t("judge.disqualified"),
        12: this.$t("judge.injured"),
        14: this.$t("judge.eliminated"),
        7: this.$t("judge.completed"),
        16: this.$t("judge.recording"),
        17: this.$t("judge.recorded"),
        20: this.$t('judge.status_20') || 'Estado 20',
        21: this.$t('judge.status_21') || 'Estado 21',
        22: this.$t('judge.status_22') || 'Editing',
        23: this.$t('judge.status_23') || this.$t('judge.no_submission') || 'No submission',
      };
      return labels[status_id] || "-";
    },

    statusClass(status_id) {
      return {
        "status-pending": status_id === 13,
        "status-scoring": status_id === 6,
        "status-disqualified": status_id === 11,
        "status-injured": status_id === 12,
        "status-completed": status_id === 7,
        "status-recording": status_id === 16,
        "status-recorded": status_id === 17,
        "status-queue": status_id === 20,
        "status-processing": status_id === 21,
        "status-review": status_id === 22 || status_id === 23,
      };
    },

    isDisabled(athlete) {
      const isPending = athlete.status_id === 13;
      const isScoring = athlete.status_id === 6;
      const isRecording = athlete.status_id === 16 && !athlete.recording_end_at;
      const isScored = athlete.status_id === 7;
      return isScoring || isRecording || isPending || isScored;
    },

    controlPop(action, id, message) {
      this.modalMessage = message;
      this.showCancel = true;
      this.pendingAction = action;
      this.pendingId = id;
      this.$refs.confirmationModal.openModal();
    },

    async confirmModal() {
      switch (this.pendingAction) {
        case 'disqualify-athlete':
          this.$emit('disqualify-athlete', this.pendingId);
          break;
        case 'injury-athlete':
          this.$emit('injury-athlete', this.pendingId);
          break;
        case 'toggle-live-service':
          await this.toggleLiveService(Boolean(this.pendingId));
          break;
        case 'assignment-blocked':
          break;
        case 'delete-recorded-video':
          this.handleDeleteRecordedVideo(this.pendingPayload);
          break;
        default:
          break;
      }

      this.pendingAction = null;
      this.pendingId = null;
      this.pendingPayload = null;
    },

    cancelModal() {
      this.pendingAction = null;
      this.pendingId = null;
      this.pendingPayload = null;
      this.selectedCompetitionId = null;
    },

    updateStatus(payload) {
      if (!payload || !payload.id || !payload.statusId) return;
      this.$emit('update-status', payload);
    },

    updateStatusForTargets(targetIds = [], statusId = null) {
      if (!statusId) return;

      [...new Set((targetIds || []).filter(Boolean))].forEach((id) => {
        this.$emit('update-status', { id, statusId });
      });
    },

    handleCameraBegin({ deviceId }) {
      const athlete = this.pendingRecordingAthlete;
      if (!athlete) return;
      const targetIds = [
        athlete.id,
        ...(Array.isArray(this.pendingRecordingPair)
          ? this.pendingRecordingPair.map((item) => item?.id)
          : []),
      ].filter(Boolean);

      this.resetTerminalUploadProgressForTargets(targetIds);
      this.cameraRecordingStarted = true;
      this.recordingInProgressId = athlete.id;
      this.$emit("start-recording-athlete", athlete, deviceId, { playlistId: this.cameraPlaylistId });
    },

    handleCameraFinish(payload) {
      const { deviceId } = payload || {};
      const hasPair = Array.isArray(this.pendingRecordingPair) && this.pendingRecordingPair.length;
      const athlete = this.pendingRecordingAthlete || (this.recordingInProgressId ? { id: this.recordingInProgressId } : null);
      const pair = hasPair ? this.pendingRecordingPair : [];

      if (hasPair) {
        pair.forEach((item) => {
          const enriched = { ...(payload || {}), playlistId: this.cameraPlaylistId };
          this.$emit("stop-recording-athlete", item, deviceId, enriched);
        });
      } else if (athlete) {
        const enriched = { ...(payload || {}), playlistId: this.cameraPlaylistId };
        this.$emit("stop-recording-athlete", athlete, deviceId, enriched);
      }

      this.finishTimersForTargets(hasPair ? pair : athlete ? [athlete] : []);

      if (payload?.blob) {
        this.prepareRecordedVideo(payload, hasPair ? pair[0] : athlete, {
          openPreview: false,
          pair,
        });
      }
      this.recordingInProgressId = null;
      this.pendingRecordingAthlete = null;
      this.pendingRecordingPair = [];
      this.cameraRecordingStarted = false;
      this.resetCameraOverlay();
      this.setActiveTimerRound(null);
    },

    handleCameraCancel() {
      const athlete = this.pendingRecordingAthlete || (this.cameraRecordingStarted ? { id: this.recordingInProgressId } : null);
      const pairIds = Array.isArray(this.pendingRecordingPair)
        ? this.pendingRecordingPair.map((item) => item?.id).filter(Boolean)
        : [];
      const shouldRevert = Boolean(athlete);
      this.cleanupCameraModal();
      if (shouldRevert) {
        this.updateStatusForTargets([athlete.id, ...pairIds], 13);
      }
    },

    finishTimersForTargets(targets = []) {
      const list = Array.isArray(targets) ? targets.filter(Boolean) : [];
      list.forEach((target) => {
        const roundId = this.resolveRoundIdForAthlete(target?.id) || target?.round_id || target?.id;
        if (!roundId) return;
        const remaining = this.getEffectiveTimerDisplaySeconds(this.timerStateByRound?.[roundId]);
        this.finishTimer(roundId, remaining);
      });
    },

    cleanupCameraModal() {
      this.showCameraModal = false;
      this.pendingRecordingAthlete = null;
      this.pendingRecordingPair = [];
      this.cameraRecordingStarted = false;
      this.recordingInProgressId = null;
      this.cameraRecordingFileName = '';
      this.setActiveTimerRound(null);
      this.resetCameraOverlay();
    },

    prepareRecordedVideo(payload = {}, athlete = null, options = {}) {
      const { openPreview = false, pair = [] } = options;
      const prevUrl = this.recordedVideoUrl;
      if (prevUrl && prevUrl.startsWith && prevUrl.startsWith('blob:')) {
        URL.revokeObjectURL(prevUrl);
      }

      let nextUrl = '';
      let fileName = payload.fileName || '';

      if (payload.blob) {
        this.recordedBlob = payload.blob;
        this.cameraRecordingFileName = payload.fileName || '';
        nextUrl = URL.createObjectURL(payload.blob);
      } else if (payload.url) {
        nextUrl = payload.url;
      }

      const targetIds = [athlete?.id || null, ...((Array.isArray(pair) ? pair : []).map((item) => item?.id))]
        .filter(Boolean);
      if (payload.blob) {
        this.clearUploadProgressForTargets(targetIds);
      }

      this.recordedVideoUrl = nextUrl;
      this.recordedVideoName = fileName;
      this.recordedAthleteId = athlete?.id || null;
      this.recordedPairIds = Array.isArray(pair) ? pair.map((item) => item?.id).filter(Boolean) : [];
      this.showRecordedPreview = Boolean(nextUrl);
      this.previewModalOpen = Boolean(nextUrl && openPreview);
    },

    openRecordedPreview(payload = {}, athlete = null) {
      this.prepareRecordedVideo(payload, athlete, {
        openPreview: true,
        pair: this.manualUploadPair,
      });
    },

    closeRecordedPreview() {
      this.previewModalOpen = false;
    },

    clearRecordedVideoState() {
      if (this.recordedVideoUrl && this.recordedVideoUrl.startsWith && this.recordedVideoUrl.startsWith('blob:')) {
        URL.revokeObjectURL(this.recordedVideoUrl);
      }
      this.recordedVideoUrl = '';
      this.recordedVideoName = '';
      this.recordedBlob = null;
      this.recordedAthleteId = null;
      this.recordedPairIds = [];
      this.showRecordedPreview = false;
      this.previewModalOpen = false;
    },

    handleDeleteRecordedVideo(payload = null) {
      const pair = this.normalizeVsPairPayload(payload?.pair);
      const athlete = pair.length ? pair[0] : payload;
      const targetIds = (pair.length > 1
        ? pair.map((item) => item?.id)
        : [athlete?.id || this.recordedAthleteId, ...(payload ? [] : (this.recordedPairIds || []))]
      ).filter(Boolean);
      const matchesCurrentRecording = payload ? this.matchesRecordedPayload(payload) : true;

      this.updateStatusForTargets(targetIds, 13);

      if (!matchesCurrentRecording) {
        if (this.manualUploadAthlete?.id && athlete?.id && String(this.manualUploadAthlete.id) === String(athlete.id)) {
          this.manualUploadFile = null;
          this.manualUploadAthlete = null;
          this.manualUploadPair = [];
          this.manualUploadMode = 'upload';
          this.manualUploadModal = false;
        }
        return;
      }

      this.manualUploadFile = null;
      this.manualUploadAthlete = null;
      this.manualUploadPair = [];
      this.manualUploadMode = 'upload';
      this.clearRecordedVideoState();
    },

    handleConfirmRecordedVideo() {
      this.uploadRecordedVideo();
    },

    matchesRecordedPayload(payload) {
      const pair = this.normalizeVsPairPayload(payload?.pair);
      if (pair.length > 1) {
        const payloadIds = pair.map((item) => String(item?.id)).filter(Boolean).sort();
        const recordedIds = (this.recordedPairIds || []).map((id) => String(id)).sort();
        return payloadIds.length > 0
          && payloadIds.length === recordedIds.length
          && payloadIds.every((id, index) => id === recordedIds[index]);
      }

      const athlete = pair.length ? pair[0] : payload;
      return Boolean(athlete?.id && this.recordedAthleteId && String(athlete.id) === String(this.recordedAthleteId));
    },

    handleUploadFromCard(payload) {
      const pair = this.normalizeVsPairPayload(payload?.pair);
      const isPairPayload = pair.length > 1;
      const athlete = isPairPayload ? pair[0] : payload;
      this.manualUploadAthlete = athlete;
      this.recordedPairIds = isPairPayload ? pair.map((p) => p?.id).filter(Boolean) : [];
      this.manualUploadPair = pair;
      this.manualUploadMode = 'upload';
      this.manualUploadFile = null;
      this.manualUploadError = '';
      this.manualUploadModal = true;
      this.$nextTick(() => this.resetManualUploadInput());
    },

    handlePreviewFromCard(payload) {
      if (this.matchesRecordedPayload(payload) && this.recordedVideoUrl) {
        this.previewModalOpen = true;
        return;
      }

      const pair = this.normalizeVsPairPayload(payload?.pair);
      const isPairPayload = pair.length > 1;
      const athlete = isPairPayload ? pair[0] : payload;
      this.manualUploadAthlete = athlete;
      this.manualUploadPair = pair;
      this.recordedPairIds = isPairPayload ? pair.map((p) => p?.id).filter(Boolean) : [];
      this.manualUploadMode = 'preview';
      this.manualUploadFile = null;
      this.manualUploadError = '';
      this.manualUploadModal = true;
      this.$nextTick(() => this.resetManualUploadInput());
      this.resetCameraOverlay();
      this.ensureTimersForTargets(isPairPayload ? pair : [athlete]);
    },

    handleDeleteFromCard(payload) {
      const pair = this.normalizeVsPairPayload(payload?.pair);
      const athlete = pair.length ? pair[0] : payload;
      const athleteName = pair.length > 1
        ? pair.map((item) => [item?.user?.name, item?.user?.surname].filter(Boolean).join(' ').trim()).filter(Boolean).join(' VS ')
        : [athlete?.user?.name, athlete?.user?.surname].filter(Boolean).join(' ').trim();

      this.modalMessage = athleteName
        ? `Se eliminara el video grabado de ${athleteName}.`
        : 'Se eliminara el video grabado de esta ronda.';
      this.showCancel = true;
      this.pendingAction = 'delete-recorded-video';
      this.pendingId = athlete?.id || null;
      this.pendingPayload = payload;
      this.$refs.confirmationModal.openModal();
    },

    onManualUploadFileChange(event) {
      this.manualUploadFile = event?.target?.files?.[0] || null;
      this.manualUploadError = '';
    },

    resetManualUploadInput() {
      const input = this.$refs.manualUploadInput;
      if (input) {
        input.value = '';
      }
    },

    async confirmManualUpload() {
      if (!this.manualUploadFile || !this.manualUploadAthlete) {
        this.manualUploadError = this.$t('competitions.messages.upload_missing_file') || 'Selecciona un archivo de video';
        return;
      }
      const isPreviewMode = this.manualUploadMode === 'preview';
      const isPairPayload = Array.isArray(this.manualUploadPair) && this.manualUploadPair.length;
      const overlayPair = isPairPayload ? this.manualUploadPair : [];
      const primaryAthleteId = this.manualUploadAthlete.id;
      const pairIds = overlayPair.map((p) => p?.id).filter(Boolean);
      const uploadPayload = { blob: this.manualUploadFile, fileName: this.manualUploadFile.name || '' };

      if (!isPreviewMode) {
        const preflight = this.getRecordedUploadPreflight({
          primaryAthleteId,
          pairIds,
          blob: this.manualUploadFile,
        });
        if (preflight.error) {
          this.manualUploadError = preflight.error;
          return;
        }
      }

      this.recordedAthleteId = primaryAthleteId;
      this.recordedBlob = this.manualUploadFile;
      this.cameraRecordingFileName = this.manualUploadFile.name || '';
      this.recordedPairIds = pairIds;
      this.resetTerminalUploadProgressForTargets([
        this.recordedAthleteId,
        ...this.recordedPairIds,
      ]);

      if (isPreviewMode) {
        this.manualUploadModal = false;
        this.manualUploadError = '';
        this.resetCameraOverlay();
        this.prepareRecordedVideo(uploadPayload, this.manualUploadAthlete, {
          openPreview: true,
          pair: overlayPair,
        });
        this.manualUploadMode = 'upload';
        this.manualUploadFile = null;
        this.manualUploadPair = [];
        this.$nextTick(() => this.resetManualUploadInput());
        return;
      }

      this.manualUploadError = '';
      const uploadResult = await this.uploadRecordedVideo();
      if (!uploadResult?.ok) {
        return;
      }

      this.manualUploadModal = false;
      this.manualUploadMode = 'upload';
      this.manualUploadFile = null;
      this.manualUploadAthlete = null;
      this.manualUploadPair = [];
      this.$nextTick(() => this.resetManualUploadInput());
    },

    cancelManualUpload() {
      this.manualUploadModal = false;
      this.manualUploadAthlete = null;
      this.manualUploadFile = null;
      this.manualUploadError = '';
      this.recordedPairIds = [];
      this.manualUploadPair = [];
      this.manualUploadMode = 'upload';
      this.$nextTick(() => this.resetManualUploadInput());
    },

    clearUploadProgressForTargets(targetIds = []) {
      if (!Array.isArray(targetIds) || !targetIds.length) return;

      const next = { ...(this.uploadProgress || {}) };
      let mutated = false;

      targetIds.filter(Boolean).forEach((id) => {
        if (!next[id]) return;
        delete next[id];
        mutated = true;
      });

      if (mutated) {
        this.uploadProgress = next;
      }
    },

    resetTerminalUploadProgressForTargets(targetIds = []) {
      if (!Array.isArray(targetIds) || !targetIds.length) return;

      const next = { ...(this.uploadProgress || {}) };
      let mutated = false;

      targetIds.filter(Boolean).forEach((id) => {
        const status = String(next[id]?.status || '').toLowerCase();
        if (!['done', 'error', 'processing_youtube'].includes(status)) return;
        delete next[id];
        mutated = true;
      });

      if (mutated) {
        this.uploadProgress = next;
      }
    },

    setUploadProgress(athleteId, payload) {
      if (!athleteId) return;
      const prev = this.uploadProgress || {};
      const nextEntry = {
        percent: payload.percent ?? prev[athleteId]?.percent ?? 0,
        status: payload.status || prev[athleteId]?.status || 'pending',
        message: payload.message || prev[athleteId]?.message || '',
      };
      this.uploadProgress = {
        ...prev,
        [athleteId]: nextEntry,
      };
    },

    setUploadProgressForTargets(targetIds, payload) {
      if (!Array.isArray(targetIds) || targetIds.length === 0) return;
      targetIds.forEach((id) => this.setUploadProgress(id, payload));
    },

    getUploadTargetIds(primaryAthleteId) {
      const ids = [primaryAthleteId, ...(this.recordedPairIds || [])].filter(Boolean);
      return [...new Set(ids)];
    },

    getRecordedUploadPreflight(payload = {}) {
      const primaryAthleteId = payload.primaryAthleteId || this.recordedAthleteId;
      const pairIds = Array.isArray(payload.pairIds) ? payload.pairIds : (this.recordedPairIds || []);
      const targetIds = [...new Set([primaryAthleteId, ...pairIds].filter(Boolean))];
      const playlistId = this.cameraPlaylistId;
      const roundIds = targetIds.map((id) => this.resolveRoundIdForAthlete(id)).filter(Boolean);
      const roundId = roundIds[0];

      if (!roundId) {
        return {
          error: this.$t('competitions.messages.upload_missing_round') || 'Round id invalido',
          targetIds,
        };
      }

      if (!playlistId) {
        return {
          error: this.$t('competitions.messages.upload_missing_playlist') || 'Playlist id invalido',
          targetIds,
        };
      }

      const authToken = this.token || (localStorage.getItem('token') || '').replace(/"/g, '');
      if (!authToken) {
        return {
          error: this.$t('competitions.messages.upload_missing_token') || 'Falta token de autenticacion',
          targetIds,
        };
      }

      const blob = payload.blob || this.recordedBlob;
      const blobSize = blob?.size || 0;
      if (!blob || blobSize === 0) {
        return {
          error: this.$t('competitions.messages.upload_missing_file') || 'Selecciona un archivo de video',
          targetIds,
        };
      }

      const allowedMimes = ['video/mp4', 'video/x-matroska', 'video/x-msvideo', 'video/webm'];
      const defaultExt = (blob?.type && blob.type.toLowerCase().includes('webm')) ? 'webm' : 'mp4';
      const normalizedMime = this.normalizeMimeType(blob?.type || '', defaultExt);
      const safeMime = allowedMimes.includes(normalizedMime) ? normalizedMime : (defaultExt === 'webm' ? 'video/webm' : 'video/mp4');

      if (!allowedMimes.includes(safeMime)) {
        return {
          error: this.$t('competitions.messages.upload_invalid_type') || 'Formato de video no soportado',
          targetIds,
        };
      }

      return {
        authToken,
        blob,
        playlistId,
        roundId,
        safeMime,
        targetIds,
      };
    },

    resolveRoundIdForAthlete(athleteId) {
      if (!athleteId) return null;
      const candidates = [];

      if (this.manualUploadAthlete && String(this.manualUploadAthlete.id) === String(athleteId)) {
        candidates.push(this.manualUploadAthlete);
      }
      if (this.pendingRecordingAthlete && String(this.pendingRecordingAthlete.id) === String(athleteId)) {
        candidates.push(this.pendingRecordingAthlete);
      }

      const matchFromRounds = (this.competitionRounds || []).find((r) => String(r?.id) === String(athleteId));
      if (matchFromRounds) {
        candidates.push(matchFromRounds);
      }

      for (const candidate of candidates) {
        const roundId = candidate?.round_id || candidate?.roundId || candidate?.round?.id || candidate?.id;
        if (roundId) return roundId;
      }

      return null;
    },

    competitionIdFromRound(roundId) {
      if (!roundId) return null;
      const match = (this.competitionRounds || []).find((r) => String(r?.id || r?.round_id) === String(roundId));
      return match?.competition_id || match?.competitionId || match?.competition?.id || null;
    },

    hydrateUploadedProgressFromRounds() {
      if (!Array.isArray(this.competitionRounds)) return;

      const next = { ...this.uploadProgress };
      let mutated = false;

      this.competitionRounds.forEach((round) => {
        const athleteKey = round?.id || round?.round_id;
        const isConfirmedUpload = [21, 10].includes(Number(round?.status_id));
        if (!athleteKey) return;

        const prev = next[athleteKey];
        if (isConfirmedUpload) {
          if (!prev || prev.status !== 'done' || prev.percent !== 100) {
            next[athleteKey] = { percent: 100, status: 'done', message: '' };
            mutated = true;
          }
          return;
        }

        if (prev?.status === 'done') {
          delete next[athleteKey];
          mutated = true;
        }
      });

      if (mutated) {
        this.uploadProgress = next;
      }
    },

    hydrateTimersFromRounds() {
      if (!Array.isArray(this.competitionRounds)) return;
      this.competitionRounds.forEach((round) => {
        const roundId = round?.id || round?.round_id;
        if (!roundId) return;
        this.applyRoundTimerState(roundId, round);
      });
    },

    extractRoundTimer(round, roundId) {
      if (!round || !roundId) return null;

      const normalizedRoundId = String(roundId);
      const timers = Array.isArray(round?.timers) ? round.timers : [];
      const directTimer = round?.timer && typeof round.timer === 'object' ? round.timer : null;

      const directMatchesRound = directTimer && String(directTimer?.round_id ?? directTimer?.id ?? round?.id ?? round?.round_id) === normalizedRoundId;
      if (directMatchesRound) {
        return this.normalizeTimerResponse(directTimer);
      }

      const timerFromList = timers.find((entry) => String(entry?.round_id ?? entry?.id) === normalizedRoundId);
      if (timerFromList) {
        return this.normalizeTimerResponse(timerFromList);
      }

      if (directTimer && !timers.length) {
        return this.normalizeTimerResponse(directTimer);
      }

      if (timers.length === 1) {
        return this.normalizeTimerResponse(timers[0]);
      }

      return null;
    },

    shouldPreferExistingTimerState(existing = null, incoming = null) {
      if (!existing || !incoming) return false;

      const existingVersion = Number(existing?.version_ms ?? existing?.version);
      const incomingVersion = Number(incoming?.version_ms ?? incoming?.version);
      if (Number.isFinite(existingVersion) && Number.isFinite(incomingVersion) && existingVersion >= incomingVersion) {
        return true;
      }

      const existingUpdatedAt = existing?.updated_at ? new Date(existing.updated_at).getTime() : NaN;
      const incomingUpdatedAt = incoming?.updated_at ? new Date(incoming.updated_at).getTime() : NaN;
      if (Number.isFinite(existingUpdatedAt) && Number.isFinite(incomingUpdatedAt) && existingUpdatedAt >= incomingUpdatedAt) {
        return true;
      }

      const existingState = String(existing?.state || '').toLowerCase();
      const incomingState = String(incoming?.state || '').toLowerCase();
      const existingHasRemaining = Number.isFinite(Number(existing?.remaining_seconds));
      const incomingHasRemaining = Number.isFinite(Number(incoming?.remaining_seconds));
      const existingIsConcrete = ['running', 'paused', 'finished'].includes(existingState);
      const incomingIsWeak = !incomingState || ['idle', 'waiting'].includes(incomingState);

      if (existingIsConcrete && existingHasRemaining && incomingIsWeak && !incomingHasRemaining) {
        return true;
      }

      if (
        Number.isFinite(existingUpdatedAt)
        && !Number.isFinite(incomingUpdatedAt)
        && Number.isFinite(existing?.duration_seconds)
      ) {
        return true;
      }

      return false;
    },

    applyRoundTimerState(roundId, roundObj = null) {
      if (!roundId) return false;
      const round = roundObj || (this.competitionRounds || []).find((r) => String(r?.id || r?.round_id) === String(roundId));
      const timer = this.extractRoundTimer(round, roundId);
      if (!timer) return false;

      const existing = this.timerStateByRound[roundId] || null;
      const existingState = String(existing?.state || '').toLowerCase();
      const incomingState = String(timer?.state || '').toLowerCase();
      const incomingVersion = Number(timer?.version_ms ?? timer?.version);
      const existingVersion = Number(existing?.version_ms ?? existing?.version);

      // During live tracking, ignore indirect round/store snapshots unless they are clearly newer.
      if (this.shouldTrackLiveTimers) {
        const existingIsLive = existingState === 'running' || existingState === 'paused';
        const incomingIsExplicitlyNewer = Number.isFinite(incomingVersion)
          && (!Number.isFinite(existingVersion) || incomingVersion > existingVersion);

        if (existingIsLive && !incomingIsExplicitlyNewer) {
          return false;
        }

        if (incomingState === 'idle' || incomingState === 'waiting') {
          return false;
        }
      }

      const mergedTimer = this.shouldPreferExistingTimerState(existing, timer)
        ? { ...timer, ...existing }
        : { ...existing, ...timer };
      const nextTimer = this.normalizeTimerResponse(mergedTimer);

      this.updateTimerState(roundId, nextTimer);
      if (Number.isFinite(nextTimer.duration_seconds)) {
        this.timerDurationInputs = { ...this.timerDurationInputs, [roundId]: nextTimer.duration_seconds };
      }
      return true;
    },

    storageKey(playlistId, roundId) {
      return `uploadResume:${playlistId}:${roundId}`;
    },

    loadStoredUpload(playlistId, roundId) {
      try {
        const raw = localStorage.getItem(this.storageKey(playlistId, roundId));
        return raw ? JSON.parse(raw) : null;
      } catch (e) {
        return null;
      }
    },

    saveStoredUpload(playlistId, roundId, payload) {
      try {
        localStorage.setItem(this.storageKey(playlistId, roundId), JSON.stringify(payload));
      } catch (e) {
        /* ignore quota errors */
      }
    },

    clearStoredUpload(playlistId, roundId) {
      try {
        localStorage.removeItem(this.storageKey(playlistId, roundId));
      } catch (e) {
        /* ignore */
      }
    },

    async fetchUploadStatus(uploadId, playlistId) {
      try {
        const resp = await this.$axios.get('/playlists/videos/status', {
          params: { playlist_id: playlistId, upload_id: uploadId },
          headers: { Authorization: `Bearer ${this.token}` },
        });
        return resp?.data || null;
      } catch (e) {
        console.warn('[Upload] status check failed', e?.response?.data || e?.message);
        return null;
      }
    },

    async hydrateStoredUploadProgress() {
      const playlistId = this.cameraPlaylistId;
      if (!playlistId || !Array.isArray(this.competitionRounds) || this.pendingResumeCheck) return;
      this.pendingResumeCheck = true;

      for (const round of this.competitionRounds) {
        const roundId = round?.id || round?.round_id;
        if (!roundId) continue;
        const stored = this.loadStoredUpload(playlistId, roundId);
        if (!stored?.upload_id) continue;

        this.setUploadProgress(roundId, {
          percent: stored.percent || 0,
          status: 'uploading',
          message: 'Pendiente de reanudar',
        });

        const status = await this.fetchUploadStatus(stored.upload_id, playlistId);
        if (!status) continue;

        if (status.status === 'uploading') {
          const percent = status.percent ?? Math.round(((status.received_chunks || 0) / (status.total_chunks || 1)) * 100);
          this.setUploadProgress(roundId, { percent, status: 'uploading', message: 'Pendiente de reanudar' });
          this.saveStoredUpload(playlistId, roundId, {
            ...stored,
            percent,
            total_chunks: status.total_chunks || stored.total_chunks,
            received_chunks: status.received_chunks,
          });
        } else if (status.status === 'completed') {
          this.setUploadProgress(roundId, { percent: 100, status: 'done' });
          this.clearStoredUpload(playlistId, roundId);
        } else if (status.status === 'not_found') {
          this.clearStoredUpload(playlistId, roundId);
        }
      }

      this.pendingResumeCheck = false;
    },

    async uploadRecordedVideo() {
      if (this.isUploadingRecorded) return { ok: false, reason: 'busy' };
      if (!this.hasRecordedBlob) {
        console.warn('[Upload] No recorded blob or athlete to upload');
        return { ok: false, reason: 'missing_blob' };
      }

      const primaryAthleteId = this.recordedAthleteId;
      const preflight = this.getRecordedUploadPreflight();
      const targetIds = preflight.targetIds || this.getUploadTargetIds(primaryAthleteId);

      if (preflight.error) {
        this.setUploadProgressForTargets(targetIds, { percent: 0, status: 'error', message: preflight.error });
        this.manualUploadError = preflight.error;
        return { ok: false, error: preflight.error };
      }

      const playlistId = preflight.playlistId;
      const roundId = preflight.roundId;
      const playlistIdNum = Number(playlistId);
      const playlistIdToSend = Number.isFinite(playlistIdNum) ? playlistIdNum : playlistId;
      const authToken = preflight.authToken;
      const blob = preflight.blob;
      const safeMime = preflight.safeMime;
      const defaultExt = safeMime === 'video/webm' ? 'webm' : 'mp4';
      const coercedFileName = this.coerceFileExtension(
        this.cameraRecordingFileName || this.recordedVideoName || `recording.${defaultExt}`,
        safeMime
      );
      const storedResume = this.loadStoredUpload(playlistIdToSend, roundId);
      let uploadId = storedResume?.upload_id || null;
      const chunkSize = storedResume?.chunk_size || this.CHUNK_SIZE;
      const maxChunkSize = 5 * 1024 * 1024; // keep chunks smaller to reduce 429s
      const effectiveChunkSize = Math.min(chunkSize, maxChunkSize);
      let totalChunks = Math.ceil(blob.size / effectiveChunkSize) || 1;
      let startChunk = 0;

      if (uploadId) {
        const status = await this.fetchUploadStatus(uploadId, playlistIdToSend);
        if (status?.status === 'uploading') {
          startChunk = status.received_chunks || 0;
          totalChunks = status.total_chunks || totalChunks;
          const percent = status.percent ?? Math.round((startChunk / totalChunks) * 100);
          this.setUploadProgressForTargets(targetIds, { percent, status: 'uploading', message: 'Reanudando subida' });
          this.saveStoredUpload(playlistIdToSend, roundId, {
            upload_id: uploadId,
            round_id: roundId,
            playlist_id: playlistIdToSend,
            total_chunks: totalChunks,
            received_chunks: startChunk,
            chunk_size: chunkSize,
            percent,
          });
        } else if (status?.status === 'completed') {
          this.setUploadProgressForTargets(targetIds, { percent: 100, status: 'done' });
          this.clearStoredUpload(playlistIdToSend, roundId);
          return { ok: true, status: 'done' };
        } else {
          uploadId = null;
          this.clearStoredUpload(playlistIdToSend, roundId);
        }
      }

      if (!uploadId) {
        uploadId = crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
        this.saveStoredUpload(playlistIdToSend, roundId, {
          upload_id: uploadId,
          round_id: roundId,
          playlist_id: playlistIdToSend,
          total_chunks: totalChunks,
          received_chunks: 0,
          chunk_size: effectiveChunkSize,
          percent: 0,
        });
      }

      this.isUploadingRecorded = true;
      this.setUploadProgressForTargets(targetIds, { percent: 0, status: 'uploading' });
      // Keep blob alive; modal already closed on confirm

      const uploadChunkWithRetry = async (chunkIndex, label = 'normal') => {
        const start = chunkIndex * effectiveChunkSize;
        const end = Math.min(start + effectiveChunkSize, blob.size);
        const chunkType = safeMime;
        const chunk = blob.slice(start, end, chunkType);
        const formData = new FormData();
        formData.append('playlist_id', playlistIdToSend);
        formData.append('upload_id', uploadId);
        formData.append('round_id', roundId);
        formData.append('chunk_index', chunkIndex);
        formData.append('total_chunks', totalChunks);
        formData.append('video', chunk, coercedFileName);

        for (let attempts = 0; attempts < 6; attempts += 1) {
          try {
            const chunkResp = await this.$axios.post('/playlists/videos/chunk', formData, {
              headers: {
                Authorization: `Bearer ${authToken}`,
                'Content-Type': 'multipart/form-data',
              },
            });
            if (chunkResp?.status !== 200) {
              throw new Error('Chunk upload failed');
            }
            break;
          } catch (err) {
            const status = err?.response?.status;
            const retriable = status === 429 || status === 503 || status === 502;
            const isLastAttempt = attempts >= 5;
            if (retriable && !isLastAttempt) {
              const backoffMs = Math.min(1000 * Math.pow(2, attempts), 8000);
              console.warn('[Upload] retry chunk', { chunkIndex, attempts: attempts + 1, status, backoffMs, label });
              await new Promise((resolve) => setTimeout(resolve, backoffMs));
              continue;
            }
            throw err;
          }
        }

        const percent = Math.round(((chunkIndex + 1) / totalChunks) * 100);
        this.setUploadProgressForTargets(targetIds, { percent, status: 'uploading' });
        this.saveStoredUpload(playlistIdToSend, roundId, {
          upload_id: uploadId,
          round_id: roundId,
          playlist_id: playlistIdToSend,
          total_chunks: totalChunks,
          received_chunks: Math.max(chunkIndex + 1, startChunk),
          chunk_size: effectiveChunkSize,
          percent,
        });
      };

      try {
        for (let chunkIndex = startChunk; chunkIndex < totalChunks; chunkIndex += 1) {
          await uploadChunkWithRetry(chunkIndex);
        }

        const finalizePayload = {
          playlist_id: playlistIdToSend,
          upload_id: uploadId,
          total_chunks: totalChunks,
          round_id: roundId,
          original_name: coercedFileName,
        };

        let finalize;
        try {
          finalize = await this.$axios.post('/playlists/videos/complete', finalizePayload, {
            headers: { Authorization: `Bearer ${authToken}` },
          });
        } catch (err) {
          const missing = err?.response?.data?.missing;
          const status = err?.response?.status;
          const hasMissing = status === 400 && Array.isArray(missing) && missing.length;

          if (hasMissing) {
            console.warn('[Upload] finalize reported missing chunks, reuploading', { missing });
            for (const idx of missing) {
              const parsedIdx = Number(idx);
              if (!Number.isFinite(parsedIdx) || parsedIdx < 0 || parsedIdx >= totalChunks) continue;
              await uploadChunkWithRetry(parsedIdx, 'reupload');
            }

            finalize = await this.$axios.post('/playlists/videos/complete', finalizePayload, {
              headers: { Authorization: `Bearer ${authToken}` },
            });
          } else {
            throw err;
          }
        }

        if (finalize?.status === 200) {
          const finalStatus = finalize?.data?.status;
          const isProcessingYouTube = finalStatus === 'processing_youtube';
          const basePayload = {
            percent: 100,
            status: isProcessingYouTube ? 'processing_youtube' : 'done',
            message: isProcessingYouTube ? (this.$t?.('competitions.youtube.processing') || 'Procesando en YouTube...') : '',
          };

          this.setUploadProgressForTargets(targetIds, basePayload);
          this.clearStoredUpload(playlistIdToSend, roundId);
          this.clearRecordedVideoState();

          if (isProcessingYouTube) {
            this.pollYoutubeProcessing({ uploadId, playlistId: playlistIdToSend, targetIds }).catch((err) => {
              console.warn('[Upload] background youtube polling failed', err?.message || err);
            });
          }
          return { ok: true, status: isProcessingYouTube ? 'processing_youtube' : 'done' };
        } else {
          throw new Error(finalize?.data?.message || 'Finalize upload failed');
        }
      } catch (error) {
        const status = error?.response?.status;
        const message = error?.response?.data?.message || error?.message || 'Upload failed';
        const prevPercent = Math.max(...targetIds.map((id) => this.uploadProgress?.[id]?.percent || 0));
        // If server says chunks are missing, clear resume metadata so the next attempt restarts from chunk 0.
        if (status === 400 && /faltan\s+chunks/i.test(message)) {
          this.clearStoredUpload(playlistIdToSend, roundId);
        }
        // If throttled, keep resume data and surface a clearer message.
        if (status === 429) {
          this.setUploadProgressForTargets(targetIds, {
            percent: prevPercent,
            status: 'error',
            message: 'Demasiadas peticiones, espera e intenta de nuevo',
          });
          this.manualUploadError = 'Demasiadas peticiones, espera e intenta de nuevo';
          console.error('[Upload] chunked upload throttled', message);
          this.isUploadingRecorded = false;
          return { ok: false, error: this.manualUploadError };
        }
        this.setUploadProgressForTargets(targetIds, {
          percent: prevPercent,
          status: 'error',
          message,
        });
        this.manualUploadError = message;
        console.error('[Upload] chunked upload failed', message);
        return { ok: false, error: message };
      } finally {
        this.isUploadingRecorded = false;
      }
    },

    async pollYoutubeProcessing({ uploadId, playlistId, targetIds }) {
      const maxAttempts = 12;
      for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
        try {
          const resp = await this.$axios.get('/playlists/videos/status', {
            params: { playlist_id: playlistId, upload_id: uploadId },
            headers: { Authorization: `Bearer ${this.token}` },
          });
          const status = resp?.data?.status;
          if (status === 'uploaded') {
            this.setUploadProgressForTargets(targetIds, { percent: 100, status: 'done', message: '' });
            return;
          }
          if (status === 'failed') {
            const msg = resp?.data?.message || 'Fallo al procesar en YouTube';
            this.setUploadProgressForTargets(targetIds, { percent: 100, status: 'error', message: msg });
            this.manualUploadError = msg;
            return;
          }
        } catch (err) {
          // ignore and keep polling
        }
        await new Promise((resolve) => setTimeout(resolve, 5000));
      }
      // If still processing after max attempts, leave processing message
      this.setUploadProgressForTargets(targetIds, {
        percent: 100,
        status: 'processing_youtube',
        message: this.$t?.('competitions.youtube.processing') || 'Procesando en YouTube...'
      });
    },


    buildRecordingFileName(athlete = {}) {
      const competitionName = this.cameraCompetitionName || '';
      const countryName = this.resolveCompetitionCountry();
      const year = this.resolveCompetitionYear();
      const headerParts = [competitionName, countryName, year].filter(Boolean);
      const header = headerParts.join(' - ');
      const athleteName = this.getAthleteFullName(athlete);
      const stageLabel = this.resolveStageSubtitle(athlete);
      const segments = [header, athleteName, stageLabel].filter(Boolean);
      return this.sanitizeFileName(segments.join(' | '));
    },

    buildVsRecordingFileName(pair = []) {
      const competitionName = this.cameraCompetitionName || '';
      const countryName = this.resolveCompetitionCountry();
      const year = this.resolveCompetitionYear();
      const headerParts = [competitionName, countryName, year].filter(Boolean);
      const header = headerParts.join(' - ');
      const names = pair.map((ath) => this.getAthleteFullName(ath)).filter(Boolean).join(' VS ');
      const stageLabel = this.resolveStageSubtitle(pair[0] || {});
      const segments = [header, names, stageLabel].filter(Boolean);
      return this.sanitizeFileName(segments.join(' | '));
    },

    resolveCompetitionCountry() {
      const competition = this.competitionData || this.competition || {};
      const info = competition.competitions_info || {};
      const country =
        info.country?.name ||
        competition.country?.name ||
        competition.country_name ||
        (typeof competition.country === 'string' ? competition.country : '');
      return country ? String(country).trim() : '';
    },

    resolveCompetitionYear() {
      const competition = this.competitionData || this.competition || {};
      const info = competition.competitions_info || {};
      const dateValue =
        info.date_start ||
        competition.date_start ||
        competition.start_date ||
        competition.competition_date ||
        competition.date ||
        '';
      if (!dateValue) return '';
      const parsed = new Date(dateValue);
      if (Number.isNaN(parsed.getTime())) return '';
      return String(parsed.getFullYear());
    },

    sanitizeFileName(value = '') {
      return String(value)
        .replace(/[\\/:*?"<>|]+/g, '-')
        .replace(/\s+/g, ' ')
        .trim();
    },

    normalizeMimeType(rawType = '', defaultExt = 'mp4') {
      const type = String(rawType || '').toLowerCase();
      if (type.includes('webm')) return 'video/webm';
      if (type.includes('mp4')) return 'video/mp4';
      if (type.includes('matroska') || type.includes('mkv')) return 'video/x-matroska';
      if (type.includes('msvideo') || type.includes('avi')) return 'video/x-msvideo';
      if (!type) {
        if (defaultExt === 'webm') return 'video/webm';
        if (defaultExt === 'mp4') return 'video/mp4';
      }
      return 'application/octet-stream';
    },

    coerceFileExtension(name = '', mime = '') {
      const lowerMime = String(mime || '').toLowerCase();
      const ext = lowerMime.includes('webm') ? 'webm'
        : lowerMime.includes('matroska') ? 'mkv'
        : lowerMime.includes('msvideo') ? 'avi'
        : 'mp4';

      const base = String(name || '').replace(/\s+\|\s+/g, ' - ');
      if (base.toLowerCase().endsWith(`.${ext}`)) return base;

      return `${base.replace(/\.[^.]+$/, '') || 'recording'}.${ext}`;
    },

    buildCameraOverlay(athlete = {}) {
      const overlayAthlete = this.resolveOverlayAthleteSource(athlete);
      const cacheKey = overlayAthlete?.id || athlete?.id;
      const cached = cacheKey ? this.countryMetaCache[cacheKey] : null;
      const meta = cached || this.extractCountryMeta(overlayAthlete);
      if (cacheKey && !cached) {
        this.countryMetaCache = {
          ...this.countryMetaCache,
          [cacheKey]: meta,
        };
      }
      return {
        athleteName: this.getAthleteFullName(overlayAthlete),
        athleteCountryName: '',
        athleteCountryCode: '',
        athleteFlagUrl: meta.flagUrl,
        stageTitle: 'Stage',
        stageSubtitle: this.resolveStageSubtitle(overlayAthlete),
        vsLeftName: '',
        vsLeftFlagUrl: '',
        vsRightName: '',
        vsRightFlagUrl: '',
      };
    },

    buildVsOverlay(pair = []) {
      const left = this.resolveOverlayAthleteSource(pair[0] || {});
      const right = this.resolveOverlayAthleteSource(pair[1] || {});
      const leftMeta = this.extractCountryMeta(left) || {};
      const rightMeta = this.extractCountryMeta(right) || {};
      return {
        athleteName: `${this.getAthleteFullName(left)} VS ${this.getAthleteFullName(right)}`.trim(),
        athleteCountryName: '',
        athleteCountryCode: '',
        athleteFlagUrl: '',
        stageTitle: 'Stage',
        stageSubtitle: this.resolveStageSubtitle(left),
        vsLeftName: this.getAthleteFullName(left),
        vsLeftFlagUrl: leftMeta.flagUrl,
        vsRightName: this.getAthleteFullName(right),
        vsRightFlagUrl: rightMeta.flagUrl,
      };
    },

    resolveOverlayAthleteSource(athlete = {}) {
      if (!athlete || typeof athlete !== 'object') return athlete;

      const roundIdCandidates = [
        athlete.id,
        athlete.round_id,
        athlete.roundId,
        athlete.round?.id,
      ].filter(Boolean).map((value) => String(value));

      const userIdCandidates = [
        athlete.user_id,
        athlete.user?.id,
      ].filter(Boolean).map((value) => String(value));

      const roundMatch = (this.competitionRounds || []).find((round) => {
        const roundIds = [round?.id, round?.round_id].filter(Boolean).map((value) => String(value));
        const roundUserIds = [round?.user_id, round?.user?.id].filter(Boolean).map((value) => String(value));
        return roundIds.some((id) => roundIdCandidates.includes(id))
          || roundUserIds.some((id) => userIdCandidates.includes(id));
      });

      if (!roundMatch) {
        return athlete;
      }

      return {
        ...roundMatch,
        ...athlete,
        user: {
          ...(roundMatch.user || {}),
          ...(athlete.user || {}),
        },
        stats: {
          ...(roundMatch.stats || {}),
          ...(athlete.stats || {}),
        },
        country: athlete.country || roundMatch.country || roundMatch.user?.country,
      };
    },

    resetCameraOverlay() {
      this.cameraOverlay = {
        athleteName: '',
        athleteCountryName: '',
        athleteCountryCode: '',
        athleteFlagUrl: '',
        stageTitle: '',
        stageSubtitle: '',
        vsLeftName: '',
        vsLeftFlagUrl: '',
        vsRightName: '',
        vsRightFlagUrl: '',
      };
    },

    extractCountryMeta(athlete = {}) {
      const stats = athlete?.stats || {};
      const user = athlete?.user || {};
      const athleteCountry = athlete?.country;
      const userCountry = user?.country;
      const normalizedAthleteCountryName = typeof athleteCountry === 'string' && athleteCountry.length > 2
        ? athleteCountry
        : athleteCountry?.name || athleteCountry?.title || '';
      const normalizedAthleteCountryCode = typeof athleteCountry === 'string' && athleteCountry.length <= 3
        ? athleteCountry
        : athleteCountry?.code || athleteCountry?.iso2 || athleteCountry?.iso_code || '';
      const normalizedUserCountryName = typeof userCountry === 'string' && userCountry.length > 2
        ? userCountry
        : userCountry?.name || userCountry?.title || '';
      const normalizedUserCountryCode = typeof userCountry === 'string' && userCountry.length <= 3
        ? userCountry
        : userCountry?.code || userCountry?.iso2 || userCountry?.iso_code || '';
      const rawCode =
        stats.country_code ||
        athlete.country_code ||
        normalizedAthleteCountryCode ||
        user.country_code ||
        normalizedUserCountryCode ||
        '';
      const countryCode = rawCode ? String(rawCode).trim().toUpperCase() : '';
      const countryName =
        stats.country_name ||
        athlete.country_name ||
        normalizedAthleteCountryName ||
        user.country_name ||
        normalizedUserCountryName ||
        countryCode;
      const remoteFlagUrl =
        stats.flag ||
        stats.flag_url ||
        athlete.flag ||
        athlete.flag_url ||
        athlete?.country?.flag ||
        athlete?.country?.flag_url ||
        user.flag ||
        user.flag_url ||
        user?.country?.flag ||
        user?.country?.flag_url ||
        '';
      const localFlagUrl = remoteFlagUrl ? '' : resolveLocalFlagAsset(countryCode);
      const flagUrl = remoteFlagUrl || localFlagUrl || '';

      if (!remoteFlagUrl && localFlagUrl) {
        console.info('[CameraOverlay] using local flag asset', {
          athleteId: athlete?.id || null,
          userId: user?.id || athlete?.user_id || null,
          countryCode,
          localFlagUrl,
        });
      } else if (!flagUrl && countryCode) {
        console.info('[CameraOverlay] missing flag asset', {
          athleteId: athlete?.id || null,
          userId: user?.id || athlete?.user_id || null,
          countryCode,
          availableRemoteFields: {
            statsFlag: stats.flag || stats.flag_url || null,
            athleteFlag: athlete.flag || athlete.flag_url || null,
            athleteCountryFlag: athlete?.country?.flag || athlete?.country?.flag_url || null,
            userFlag: user.flag || user.flag_url || null,
            userCountryFlag: user?.country?.flag || user?.country?.flag_url || null,
          },
        });
      }

      return {
        countryName: countryName || '',
        countryCode,
        flagUrl: flagUrl || '',
        flag: flagUrl || '',
      };
    },

    getAthleteFullName(athlete = {}) {
      const user = athlete?.user || {};
      const parts = [user.name || athlete.name, user.surname || athlete.surname].filter(Boolean);
      return parts.join(' ').trim();
    },

    resolveStageSubtitle(athlete = {}) {
      const isDualQualification = Boolean(this.competition?.is_qualification_dual);
      const isVsCompetition = Number(this.competition?.is_vs) === 1;

      if (this.selectedRoundNumber) {
        return isDualQualification
          ? `Qualification ${this.selectedRoundNumber}`
          : 'Qualification';
      }

      const roundNumber = athlete.round_number || athlete.roundNumber;
      if (roundNumber === 1) {
        return isDualQualification ? 'Qualification 1' : 'Qualification';
      }
      if (roundNumber === 2 && isDualQualification) {
        return 'Qualification 2';
      }

      const stageMapById = {
        1: 'Final',
        2: 'Semifinal',
        3: 'Quarterfinals',
        4: 'Round of 8',
        5: 'Round of 16',
        6: 'Round of 32',
      };

      const stageIdRaw = this.selectedStageName || athlete.round_size_id;
      const stageId = stageIdRaw ? Number(stageIdRaw) : null;

      if (isVsCompetition && stageId === 1) {
        const vsGroup = String(athlete?.vs_group || '').trim().toUpperCase();

        if (['3P', 'TP', 'BRONZE'].includes(vsGroup)) {
          return '3rd Place';
        }

        if (['F', 'GF', 'FINAL'].includes(vsGroup)) {
          return 'Final';
        }
      }

      if (stageId && stageMapById[stageId]) {
        return stageMapById[stageId];
      }

      const stage = this.stages?.find((s) => s.id === Number(stageId));
      const stageSize = stage?.round_size || stage?.size || stage?.roundSize;
      if (stageSize) {
        if (stageSize === 32) return 'Round of 32';
        if (stageSize === 16) return 'Round of 16';
        if (stageSize === 8) return 'Quarterfinals';
        if (stageSize === 4) return 'Semifinal';
        if (stageSize === 2) return 'Final';
      }

      const label = stage?.name || stage?.title || stage?.stage_name;
      if (label) return String(label);

      if (stageId) return `Stage ${stageId}`;
      return 'Unknown stage';
    },

    codeToFlag(code = '') {
      if (!code || code.length < 2) {
        return '';
      }
      const letters = code
        .toUpperCase()
        .replace(/[^A-Z]/g, '')
        .slice(0, 2);
      if (letters.length < 2) {
        return '';
      }
      return letters
        .split('')
        .map((char) => String.fromCodePoint(127397 + char.charCodeAt(0)))
        .join('');
    },

    eliminationRef(stageName) {
      if (!this.refsEliminations[stageName]) {
        this.$set(this.refsEliminations, stageName, null);
      }
      return stageName;
    },

    decorateRoundWithFeedback(round) {
      const countFromRound = round?.negative_feedback_count;
      const countFromFeedbackField = round?.athlete_feedback_twos ?? round?.athlete_feedback_two ?? round?.feedback_two ?? round?.feedback_twos;
      const mergedCount = countFromRound ?? countFromFeedbackField;

      if (mergedCount === undefined) return round;

      return {
        ...round,
        negative_feedback_count: mergedCount,
        has_feedback: round?.has_feedback ?? (mergedCount !== undefined ? Number(mergedCount) > 0 : undefined),
      };
    },

    scrollToSection() {
      this.$nextTick(() => {
        const el = this.$refs.sectionContainer;
        if (el && typeof el.scrollIntoView === 'function') {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    },

    async handlePrepareYouTube() {
      const competitionId = this.competition?.id || this.competitionIdToView;
      if (!competitionId) {
        this.youtubeStatusMessage = this.$t('competitions.youtube.error_competition_missing');
        return;
      }

      this.youtubePreparing = true;
      this.youtubeStatusMessage = this.$t('competitions.youtube.status_preparing');
      this.youtubeIssues = [];

      try {
        const response = await this.$axios.post(`/competitions/${competitionId}/playlist/prepare`, {}, {
          headers: { Authorization: `Bearer ${this.token}` },
        });

        if (response?.data?.success) {
          this.youtubePlaylistId = response.data.playlist_id || response.data.id || '';
          this.youtubeStatusMessage = response?.data?.message || this.$t('competitions.youtube.playlist_created');
          this.youtubeIssues = response.data.issues || [];
          this.imageUploadedAt = response.data.image_uploaded_at || response.data.imagen_upload_at || null;
          this.youtubeEnabled = true;
        } else {
          this.youtubeStatusMessage = response?.data?.message || this.$t('competitions.youtube.prepare_failed');
          this.youtubeIssues = response?.data?.issues || [];
        }
      } catch (error) {
        const status = error?.response?.status;
        if (status === 503) {
          this.youtubeStatusMessage = this.$t('competitions.youtube.error_youtube_503');
        } else if (status === 502) {
          this.youtubeStatusMessage = this.$t('competitions.youtube.error_prepare_502');
        } else {
          this.youtubeStatusMessage = error?.response?.data?.message || this.$t('competitions.youtube.error_prepare_generic');
        }
        this.youtubeIssues = error?.response?.data?.issues || [];
      } finally {
        this.youtubePreparing = false;
      }
    },

    handleCameraReady() {
      this.cameraReady = true;
    },

    handleCameraStopped() {
      this.cameraReady = false;
      this.pendingRecordingAthlete = null;
      this.pendingRecordingPair = [];
      this.recordingInProgressId = null;
      this.cameraRecordingStarted = false;
      this.cameraRecordingFileName = '';
      this.setActiveTimerRound(null);
      this.resetCameraOverlay();
    },

    async handleUploadBanner() {
      const competitionId = this.competition?.id || this.competitionIdToView;
      if (!competitionId) {
        this.imageIssues = [this.$t('competitions.youtube.error_competition_missing')];
        return;
      }
      if (!this.hasPlaylist) {
        this.imageIssues = [this.$t('competitions.youtube.error_prepare_first')];
        return;
      }

      this.imageUploading = true;
      this.imageIssues = [];

      try {
        const response = await this.$axios.post(`/competitions/${competitionId}/playlist/image`, {}, {
          headers: { Authorization: `Bearer ${this.token}` },
        });

        if (response?.data?.success) {
          this.imageUploadedAt = response.data.image_uploaded_at || response.data.imagen_upload_at || new Date().toISOString();
          this.youtubeStatusMessage = response?.data?.message || this.$t('competitions.youtube.banner_upload_success');
          this.imageIssues = response?.data?.issues || [];
        } else {
          this.youtubeStatusMessage = response?.data?.message || this.$t('competitions.youtube.banner_upload_failed');
          this.imageIssues = response?.data?.issues || [];
        }
      } catch (error) {
        const status = error?.response?.status;
        if (status === 503) {
          this.youtubeStatusMessage = this.$t('competitions.youtube.error_youtube_503');
        } else if (status === 502) {
          this.youtubeStatusMessage = this.$t('competitions.youtube.error_banner_502');
        } else {
          this.youtubeStatusMessage = error?.response?.data?.message || this.$t('competitions.youtube.error_banner_generic');
        }
        this.imageIssues = error?.response?.data?.issues || [];
      } finally {
        this.imageUploading = false;
      }
    },

    formatDate(dateStr) {
      if (!dateStr) return '';
      const d = new Date(dateStr);
      if (Number.isNaN(d.getTime())) return '';
      return d.toLocaleString();
    },
  },
};
</script>

<style scoped>
.competition-judge {
  width: 100%;
  margin-top: 20px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  padding: 16px;
}

.no-athletes {
  text-align: center;
  font-style: italic;
  color: gray;
}
.competition-rounds {
  margin-top: 20px;
}

.round-buttons, .stage-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 15px;
}

.round-toggle {
  padding: 8px 12px;
  border: none;
  background: #007bff;
  color: white;
  border-radius: 4px;
  cursor: pointer;
  max-width: 150px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.round-toggle:hover {
  background: #0056b3;
}

.best-result {
  background: #28a745;
}

.best-result:hover {
  background: #218838;
}

.stage-toggle {
  background: #ff9800;
}

.stage-toggle:hover {
  background: #e68900;
}

.truncate-text {
  display: block;
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-bottom: 1rem;
}

.judge-operations-banner {
  display: grid;
  gap: 1rem;
  margin-bottom: 1rem;
  padding: 1.1rem 1.2rem 1.2rem;
  border: 1px solid #d9e6ff;
  border-radius: 20px;
  background:
    radial-gradient(circle at top left, rgba(59, 130, 246, 0.16), transparent 28%),
    linear-gradient(135deg, #f8fbff 0%, #edf4ff 55%, #f7fbff 100%);
  box-shadow: 0 14px 30px rgba(37, 99, 235, 0.08);
}

.judge-operations-banner__header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
}

.judge-operations-banner__header h4 {
  margin: 0 0 0.35rem;
  font-size: 1.1rem;
}

.judge-operations-banner__header p {
  margin: 0;
  color: #475569;
  max-width: 44rem;
}

.judge-operations-banner__eyebrow {
  margin: 0 0 0.35rem;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #64748b;
}

.judge-operations-banner__summary-pill {
  display: inline-flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: center;
  min-width: 110px;
  padding: 0.75rem 0.9rem;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.82);
  border: 1px solid rgba(59, 130, 246, 0.15);
  color: #1e3a8a;
}

.judge-operations-banner__summary-pill strong {
  font-size: 1.4rem;
  line-height: 1;
}

.judge-operations-banner__summary-pill span {
  font-size: 0.82rem;
  color: #475569;
  text-align: right;
}

.judge-operations-banner__track {
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: minmax(280px, 320px);
  gap: 0.9rem;
  overflow-x: auto;
  padding-bottom: 0.35rem;
  scroll-snap-type: x proximity;
}

.judge-operations-banner__track::-webkit-scrollbar {
  height: 8px;
}

.judge-operations-banner__track::-webkit-scrollbar-thumb {
  background: rgba(148, 163, 184, 0.5);
  border-radius: 999px;
}

.judge-operations-card {
  display: grid;
  gap: 0.9rem;
  min-height: 184px;
  padding: 1rem;
  border-radius: 18px;
  border: 1px solid rgba(148, 163, 184, 0.18);
  background: rgba(255, 255, 255, 0.94);
  box-shadow: 0 12px 28px rgba(15, 23, 42, 0.07);
  scroll-snap-align: start;
}

.judge-operations-card--clickable {
  cursor: pointer;
  transition: transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease;
}

.judge-operations-card--clickable:hover,
.judge-operations-card--clickable:focus-visible {
  transform: translateY(-2px);
  box-shadow: 0 18px 36px rgba(15, 23, 42, 0.12);
  border-color: rgba(37, 99, 235, 0.28);
  outline: none;
}

.judge-operations-card--current {
  border-color: rgba(245, 158, 11, 0.35);
  background: linear-gradient(180deg, rgba(255, 251, 235, 0.96), rgba(255, 255, 255, 0.98));
}

.judge-operations-card--next {
  border-color: rgba(59, 130, 246, 0.3);
  background: linear-gradient(180deg, rgba(239, 246, 255, 0.96), rgba(255, 255, 255, 0.98));
}

.judge-operations-card--queued {
  border-color: rgba(148, 163, 184, 0.22);
}

.judge-operations-card__topline,
.judge-operations-card__footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
}

.judge-operations-card__priority,
.judge-operations-card__status,
.judge-operations-card__judge,
.judge-operations-card__position {
  display: inline-flex;
  align-items: center;
  min-height: 30px;
  padding: 0.2rem 0.65rem;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 700;
}

.judge-operations-card__priority {
  background: #dbeafe;
  color: #1d4ed8;
}

.judge-operations-card--current .judge-operations-card__priority {
  background: #fef3c7;
  color: #b45309;
}

.judge-operations-card__status {
  background: #f1f5f9;
  color: #475569;
}

.judge-operations-card__body {
  display: grid;
  gap: 0.35rem;
}

.judge-operations-card__body strong {
  font-size: 1.05rem;
  line-height: 1.2;
}

.judge-operations-card__body p {
  margin: 0;
  color: #64748b;
  font-size: 0.9rem;
}

.judge-operations-card__judge {
  background: #eef2ff;
  color: #4338ca;
  max-width: calc(100% - 56px);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.judge-operations-card__timer {
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  min-width: 4.5rem;
  padding: 0;
  border-radius: 0;
  background: transparent;
  color: #c2410c;
  border: 0;
  font-size: 0.9rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.judge-operations-card__position {
  background: #f8fafc;
  color: #64748b;
}

@media (max-width: 991px) {
  .judge-operations-banner {
    padding: 1rem;
  }

  .judge-operations-banner__header {
    flex-direction: column;
  }

  .judge-operations-banner__summary-pill {
    align-items: flex-start;
  }

  .judge-operations-banner__track {
    grid-auto-columns: minmax(240px, 86vw);
  }
}

.advance-btn {
  position: relative;
  transition: all 0.3s ease;
}

.icon-arrow {
  margin-left: 0.5rem;
  transition: transform 0.3s ease;
}
.round-toggle.active {
  background-color: #000;
  color: white;
  font-weight: bold;
}
.round-toggle.active {
  background-color: #000;
  color: white;
  font-weight: bold;
}

.advance-btn:hover .icon-arrow {
  transform: translateX(5px);
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2500;
}

.modal-card {
  background: #fff;
  border-radius: 8px;
  padding: 16px 18px;
  max-width: 420px;
  width: 90%;
  box-shadow: 0 6px 18px rgba(0,0,0,0.18);
}


.recording-top-row {
  display: grid;
  gap: 16px;
  width: 100%;
  margin: 12px 0 16px 0;
  align-items: start;
  grid-template-columns: minmax(0, 1fr);
}

.recording-top-row--single {
  grid-template-columns: minmax(180px, 240px) minmax(0, 1fr);
  column-gap: 20px;
}

.recording-top-row--vs {
  grid-template-columns: minmax(118px, 150px) minmax(0, 1fr) minmax(118px, 150px);
  column-gap: 28px;
}

.recording-inline {
  min-width: 0;
}

.recording-inline--priority {
  grid-column: auto;
  overflow: visible;
  position: relative;
  z-index: 30;
}

.recorded-inline-card {
  grid-column: 1 / -1;
  width: 100%;
  align-self: stretch;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 12px 14px;
  box-shadow: 0 4px 10px rgba(0,0,0,0.08);
}

.recorded-video-modal {
  max-width: 820px;
  width: 94%;
}

.recorded-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
}

.recorded-video-name {
  font-size: 0.95rem;
  color: #495057;
  margin-bottom: 12px;
  word-break: break-word;
}

.recording-layout {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.recording-slider-col {
  display: flex;
  flex-direction: column;
}

.recorded-inline {
  margin-top: 12px;
  background: transparent;
  border: none;
  padding: 0;
}

.recorded-inline-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 8px;
}

.recorded-inline h5 {
  margin: 0;
}

/* Embed CameraModal inline instead of overlay */
.recording-inline :deep(.camera-modal-backdrop) {
  position: static;
  background: transparent;
  width: 100%;
  height: auto;
  padding: 0;
}

.recording-inline :deep(.camera-modal-card) {
  box-shadow: none;
  width: 100%;
  min-width: 0;
  max-width: none;
  padding: 0;
  border: none;
  overflow: visible;
}

.recording-inline :deep(.camera-info-bar) {
  overflow: visible;
}

.recording-inline :deep(.camera-modal-header) {
  border-bottom: 1px solid #e5e7eb;
}

.recording-inline :deep(.camera-modal-close) {
  display: none;
}
.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}


.timer-control-panel {
  display: grid;
  gap: 12px;
  margin-top: 0;
  width: 100%;
  align-self: stretch;
  min-width: 0;
  justify-self: stretch;
}

.timer-control-panel--compact {
  align-content: start;
}

.timer-control-cards.timer-control-cards--stacked {
  grid-template-columns: 1fr;
  min-width: 0;
}

.timer-control-panel--side .timer-control-card,
.timer-control-panel--single .timer-control-card {
  min-height: 100%;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
}

.timer-control-panel--side {
  min-width: 0;
  max-width: 150px;
  overflow: hidden;
}

.timer-control-panel--single {
  min-width: 0;
  overflow: hidden;
}

.timer-control-panel--side .timer-control-card {
  padding: 8px 8px 10px;
}

.timer-control-panel--side .timer-control-card__header {
  margin-bottom: 6px;
}

.timer-control-panel--side .timer-control-card__label {
  display: block;
  width: 100%;
  font-size: 0.8rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.timer-control-panel--side .timer-control-card__pill {
  padding: 8px 6px;
  font-size: 1.05rem;
  margin-bottom: 8px;
}

.timer-control-panel--side .timer-toggle-btn {
  padding: 12px 8px;
  font-size: 1rem;
}

.timer-control-panel--side .timer-control-card__footer {
  margin-top: 8px;
}

.timer-control-panel--side .timer-control-card__state {
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.timer-control-card {
  background: #0f172a;
  color: #f8fafc;
  border-radius: 12px;
  padding: 10px 12px 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.18);
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.timer-control-card__header {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  font-weight: 700;
  margin-bottom: 4px;
}

.timer-control-card__label {
  font-size: 0.95rem;
}

.timer-control-card__state {
  font-size: 0.75rem;
  padding: 3px 6px;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.12);
  text-transform: uppercase;
}

.timer-control-card__pill {
  background: #111827;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 10px;
  padding: 8px 12px;
  font-size: 1.4rem;
  font-variant-numeric: tabular-nums;
  text-align: center;
  margin-bottom: 10px;
}

.timer-control-card__actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: stretch;
}

.timer-control-card__input {
  display: flex;
  align-items: center;
  gap: 8px;
}

.timer-control-card__input input {
  width: 80px;
  padding: 4px 6px;
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: #0b1220;
  color: #f8fafc;
}

.timer-control-card__hint {
  font-size: 0.8rem;
  color: #cbd5e1;
}

.timer-control-card__buttons {
  display: flex;
  gap: 8px;
}

.timer-toggle-btn {
  width: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0;
  padding: 16px 12px;
  border: none;
  border-radius: 10px;
  font-weight: 800;
  font-size: 1.25rem;
  letter-spacing: 0.2px;
  text-transform: uppercase;
  cursor: pointer;
  transition: transform 120ms ease, box-shadow 120ms ease, background 120ms ease;
}

.timer-toggle-btn.is-idle {
  background: linear-gradient(135deg, #22c55e, #16a34a);
  color: #0b1727;
  box-shadow: 0 8px 20px rgba(34, 197, 94, 0.25);
}

.timer-toggle-btn.is-running {
  background: linear-gradient(135deg, #f97316, #ea580c);
  color: #0b0f1a;
  box-shadow: 0 8px 20px rgba(234, 88, 12, 0.25);
}

.timer-toggle-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  box-shadow: none;
}

.timer-toggle-btn:not(:disabled):hover {
  transform: translateY(-1px);
}

.timer-control-card__footer {
  margin-top: 10px;
  display: flex;
  justify-content: center;
}

.timer-control-toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
  position: relative;
}

.timer-control-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 12px;
  min-width: 0;
}

@media (max-width: 1199px) {
  .recording-top-row--single,
  .recording-top-row--vs {
    grid-template-columns: 1fr;
  }
}

.timer-gear-anchor {
  position: relative;
  display: flex;
  align-items: center;
  overflow: visible;
  z-index: 60;
}

.timer-gear-menu {
  position: absolute;
  top: 36px;
  right: 0;
  z-index: 4000;
}

.timer-gear-menu__card {
  background: #ffffff;
  color: #0f172a;
  border: 1px solid #d1d5db;
  border-radius: 10px;
  padding: 10px 12px;
  min-width: 260px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.18);
  z-index: 1;
}

.timer-gear-menu__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 10px;
}

.timer-theme-selector {
  display: flex;
  width: 100%;
  gap: 8px;
}

.timer-theme-selector .btn {
  flex: 1 1 0;
}

.timer-gear-menu__label {
  font-weight: 600;
  font-size: 0.9rem;
  white-space: nowrap;
}

.timer-gear-menu__input {
  display: flex;
  align-items: center;
  gap: 8px;
}

.timer-gear-menu__input input {
  width: 90px;
  padding: 4px 6px;
  border-radius: 6px;
  border: 1px solid #cbd5e1;
  background: #f8fafc;
  color: #0f172a;
}

.timer-gear-menu__resets {
  align-items: center;
}

.timer-gear-menu__reset-buttons {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.timer-reset-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 200px;
  overflow: hidden;
  white-space: nowrap;
}

.timer-reset-btn__text {
  overflow: hidden;
  text-overflow: ellipsis;
}

.timer-reset-btn--icon-only {
  justify-content: center;
  width: 36px;
  min-width: 36px;
  padding-left: 0;
  padding-right: 0;
}

.timer-save-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
</style>